import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  type DragEndEvent,
  type DragStartEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { format, parseISO } from 'date-fns';
import {
  ArrowLeft,
  Calendar as CalendarIcon,
  ChevronDown,
  ChevronUp,
  GripVertical,
  Loader2,
  PanelRight,
  Plus,
  Rocket,
  Save,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FieldCanvasPreview } from '@/components/forms/FieldCanvasPreview';
import {
  FieldLogicConditionsSection,
  type FieldConditionalLogicBlock,
  type FieldLogicAction,
} from '@/components/forms/FieldLogicConditionsSection';
import { FormPreview } from '@/components/forms/FormPreview';
import { AppointmentConfigEditor } from '@/components/forms/AppointmentConfigEditor';
import { listSquarePaymentFields } from '@/lib/form-builder/square-payment-fields';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { dynamicFormsApi } from '@/lib/api/dynamic-forms';
import { useIsMobile } from '@/hooks/use-mobile';
import { createDefaultField, normalizeField } from '@/lib/form-builder/defaults';
import {
  getChoiceOptions,
  getMaxSelections,
  getMinSelections,
  patchChoiceOptions,
} from '@/lib/form-builder/field-validation';
import {
  PALETTE_GROUPS,
  fieldTypeFromPaletteId,
  isPaletteDragId,
  paletteDragId,
} from '@/lib/form-builder/palette';
import type { DynamicForm } from '@/types/dynamic-form';
import type {
  FieldOption,
  FormDefinition,
  FormField,
  FormFieldTypeId,
} from '@/types/form-definition';
import { cn } from '@/lib/utils';

type PublishInspectorTab = 'basics' | 'layout' | 'validation' | 'config' | 'logic';

type PublishValidationIssue = {
  message: string;
  focusFieldId?: string;
  focusInspectorTab?: PublishInspectorTab;
};

const CANVAS_DROP_ID = 'form-builder-canvas';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function toDateOnlyString(d: Date) {
  return format(d, 'yyyy-MM-dd');
}

function parseDateOnly(value: unknown): Date | undefined {
  if (typeof value !== 'string' || !value) return undefined;
  try {
    // Prefer strict YYYY-MM-DD; parseISO handles it.
    const d = parseISO(value);
    return Number.isNaN(d.getTime()) ? undefined : d;
  } catch {
    return undefined;
  }
}

function normalizeFields(draft: FormDefinition | undefined): FormField[] {
  if (!draft?.fields?.length) return [];
  return draft.fields.map((f) => normalizeField({ ...f }));
}

function fieldSummary(f: FormField): string {
  if (f.type === 'paragraph' && f.html) {
    const t = f.html.replace(/<[^>]+>/g, '').trim();
    return t.slice(0, 80) || 'Paragraph';
  }
  return f.label || f.type;
}

function CanvasDropArea({ children }: { children: ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: CANVAS_DROP_ID });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'min-h-[260px] rounded-2xl border-2 border-dashed bg-muted/15 p-4 transition-colors',
        isOver ? 'border-primary/50 bg-primary/5' : 'border-border/60'
      )}
    >
      {children}
    </div>
  );
}

function PaletteDraggable({
  type,
  label,
  icon: Icon,
  onAddField,
}: {
  type: FormFieldTypeId;
  label: string;
  icon: ComponentType<{ className?: string }>;
  onAddField: (type: FormFieldTypeId) => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: paletteDragId(type),
    data: { fieldType: type },
  });
  return (
    <button
      type="button"
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={() => {
        if (isDragging) return;
        onAddField(type);
      }}
      className={cn(
        'flex w-full items-center gap-3 rounded-lg border border-border/80 bg-card px-3 py-2.5 text-left text-sm shadow-sm transition-colors',
        'hover:border-primary/35 hover:bg-accent/40',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isDragging && 'opacity-60'
      )}
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
        <Icon className="h-4 w-4" />
      </span>
      <span className="font-medium leading-tight">{label}</span>
    </button>
  );
}

function SortableFieldRow({
  field,
  selected,
  onSelect,
  index,
  total,
  onMove,
  onDelete,
}: {
  field: FormField;
  selected: boolean;
  onSelect: () => void;
  index: number;
  total: number;
  onMove: (opts: { id: string; direction: 'up' | 'down' }) => void;
  onDelete: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div ref={setNodeRef} style={style} className="touch-none" data-outline-field-id={field.id}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => onSelect()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect();
          }
        }}
        className={cn(
          'group flex w-full cursor-pointer items-stretch gap-0 overflow-hidden rounded-xl border bg-card text-left shadow-sm transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring',
          selected
            ? 'border-primary ring-2 ring-primary/20'
            : 'border-border/60 hover:border-border/80 hover:bg-accent/20'
        )}
      >
        <div className="flex w-10 shrink-0 flex-col items-stretch border-r border-border/60 bg-muted/20 text-muted-foreground transition-colors group-hover:bg-muted/30">
          <button
            type="button"
            className={cn(
              'flex h-8 items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
              index === 0
                ? 'cursor-not-allowed opacity-40'
                : 'hover:bg-accent/40'
            )}
            disabled={index === 0}
            aria-label="Move field up"
            onClick={(e) => {
              e.stopPropagation();
              onMove({ id: field.id, direction: 'up' });
            }}
          >
            <ChevronUp className="h-4 w-4" />
          </button>

          <button
            type="button"
            className="flex h-10 cursor-grab items-center justify-center transition-colors hover:bg-accent/30 active:cursor-grabbing focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
            aria-label="Drag to reorder"
            {...attributes}
            {...listeners}
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical className="h-4 w-4" />
          </button>

          <button
            type="button"
            className={cn(
              'flex h-8 items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
              index === total - 1
                ? 'cursor-not-allowed opacity-40'
                : 'hover:bg-accent/40'
            )}
            disabled={index === total - 1}
            aria-label="Move field down"
            onClick={(e) => {
              e.stopPropagation();
              onMove({ id: field.id, direction: 'down' });
            }}
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>
        <div className="min-w-0 flex-1 px-3 py-3">
          <div className="mb-2 flex items-start justify-between gap-2">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="shrink-0 rounded-md border border-border/60 bg-muted/30 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-foreground">
                #{index + 1}/{total}
              </span>
              <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{field.type}</p>
            </div>
            <button
              type="button"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-transparent text-muted-foreground transition-colors hover:border-border/60 hover:bg-accent/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
              aria-label="Delete field"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(field.id);
              }}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
          <div className="pointer-events-none select-none">
            <FieldCanvasPreview field={field} />
          </div>
        </div>
      </div>
      {isDragging ? <div className="h-2" /> : null}
    </div>
  );
}

function FormElementsPalette({ onAddField }: { onAddField: (type: FormFieldTypeId) => void }) {
  return (
    <div className="space-y-5 py-1">
      {PALETTE_GROUPS.map((group) => (
        <div key={group.id}>
          <p className="mb-2 px-1 text-xs font-medium text-muted-foreground">{group.title}</p>
          <div className="space-y-2">
            {group.items.map((item) => (
              <PaletteDraggable
                key={item.type}
                type={item.type}
                label={item.label}
                icon={item.icon}
                onAddField={onAddField}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ChoiceOptionsEditor({
  field,
  allFields,
  onUpdateField,
}: {
  field: FormField;
  allFields: FormField[];
  onUpdateField: (id: string, patch: Partial<FormField>) => void;
}) {
  const options = getChoiceOptions(field);
  const apply = (next: FieldOption[]) =>
    onUpdateField(field.id, patchChoiceOptions(field, next));
  const duplicateValues = (() => {
    const counts = new Map<string, number>();
    for (const o of options) {
      const key = String(o.value ?? '').trim();
      if (!key) continue;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .filter(([, c]) => c > 1)
      .map(([v]) => v);
  })();

  return (
    <div className="grid gap-2">
      <Label>Choices</Label>
      <p className="text-[11px] leading-snug text-muted-foreground">
        Stored in <span className="font-mono text-[10px]">config.options</span> (value / label).{' '}
        <span className="font-mono text-[10px]">validation.allowedValues</span> is kept in sync for submit
        validation.
      </p>
      {duplicateValues.length > 0 ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          Duplicate option values are not allowed: <span className="font-mono">{duplicateValues.join(', ')}</span>
        </div>
      ) : null}
      <div className="space-y-3">
        {options.map((opt, i) => (
          <div
            key={`${field.id}-opt-${i}`}
            className="space-y-2 rounded-xl border border-border/70 bg-card p-3 shadow-sm"
          >
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="grid gap-1">
                <Label className="text-[10px] uppercase text-muted-foreground">Value</Label>
                <Input
                  value={opt.value}
                  onChange={(e) => {
                    const prevValue = String(opt.value ?? '').trim();
                    const next = options.map((o, j) =>
                      j === i ? { ...o, value: e.target.value } : o
                    );
                    apply(next);

                    // Warn if this option value is referenced by logic elsewhere.
                    if (prevValue) {
                      let refs = 0;
                      for (const target of allFields) {
                        const logic = Array.isArray(target.logic) ? target.logic : [];
                        for (const block of logic as any[]) {
                          if (!block || typeof block !== 'object') continue;
                          const when = Array.isArray((block as any).when) ? ((block as any).when as any[]) : [];
                          for (const clause of when) {
                            if (!clause || typeof clause !== 'object') continue;
                            if (clause.fieldId !== field.id) continue;
                            if (clause.op === 'eq' && clause.value === prevValue) refs += 1;
                            if (clause.op === 'in' && Array.isArray(clause.values) && clause.values.includes(prevValue)) refs += 1;
                          }
                        }
                      }
                      if (refs > 0) {
                        toast.message('Option value is used in logic', {
                          description: `This value is referenced by ${refs} condition(s). Update the affected rules to use the new value.`,
                        });
                      }
                    }
                  }}
                  className="font-mono text-xs"
                />
              </div>
              <div className="grid gap-1">
                <Label className="text-[10px] uppercase text-muted-foreground">Label</Label>
                <Input
                  value={opt.label}
                  onChange={(e) => {
                    const next = options.map((o, j) =>
                      j === i ? { ...o, label: e.target.value } : o
                    );
                    apply(next);
                  }}
                />
              </div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id={`${field.id}-opt-dis-${i}`}
                  checked={Boolean(opt.disabled)}
                  onCheckedChange={(checked) => {
                    const next = options.map((o, j) =>
                      j === i ? { ...o, disabled: checked === true } : o
                    );
                    apply(next);
                  }}
                />
                <Label htmlFor={`${field.id}-opt-dis-${i}`} className="text-xs font-normal text-muted-foreground">
                  Disabled
                </Label>
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8 shrink-0"
                disabled={options.length <= 1}
                onClick={() => {
                  const next = options.filter((_, j) => j !== i);
                  apply(next.length ? next : [{ value: 'Option 1', label: 'Option 1' }]);
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => {
            const n = options.length + 1;
            apply([...options, { value: `option_${n}`, label: `Option ${n}` }]);
          }}
        >
          Add choice
        </Button>
      </div>
    </div>
  );
}

function normalizeCaptchaProvider(v: unknown): 'none' | 'jotform' | 'recaptcha' {
  if (v === 'jotform' || v === 'recaptcha' || v === 'none') return v;
  return 'none';
}

function FormSettingsFields({
  settings,
  onChange,
}: {
  settings: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const uid = useId();
  const submitButtonLabel = typeof settings.submitButtonLabel === 'string' ? settings.submitButtonLabel : '';
  const thankYouMessage = typeof settings.thankYouMessage === 'string' ? settings.thankYouMessage : '';
  const redirectUrl = typeof settings.redirectUrl === 'string' ? settings.redirectUrl : '';
  const recaptchaSiteKey = typeof settings.recaptchaSiteKey === 'string' ? settings.recaptchaSiteKey : '';
  const captchaProvider = normalizeCaptchaProvider(settings.captchaProvider);

  const patch = (p: Record<string, unknown>) => {
    const next = { ...settings, ...p };
    for (const k of Object.keys(p)) {
      if (p[k] === undefined) delete next[k];
    }
    onChange(next);
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Submit & captcha</p>
        <p className="pt-1 text-[11px] leading-snug text-muted-foreground">
          Post-submit messaging and captcha (API §3.4).
        </p>
      </div>
      <div className="grid gap-2">
        <Label htmlFor={`${uid}-submit-lbl`}>Submit button label</Label>
        <Input
          id={`${uid}-submit-lbl`}
          value={submitButtonLabel}
          onChange={(e) => patch({ submitButtonLabel: e.target.value || undefined })}
          placeholder="e.g. Send"
          maxLength={200}
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor={`${uid}-thanks`}>Thank you message</Label>
        <Textarea
          id={`${uid}-thanks`}
          rows={3}
          value={thankYouMessage}
          onChange={(e) => patch({ thankYouMessage: e.target.value || undefined })}
          placeholder="Shown after successful submission"
          maxLength={4000}
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor={`${uid}-redirect`}>Redirect URL</Label>
        <Input
          id={`${uid}-redirect`}
          type="url"
          value={redirectUrl}
          onChange={(e) => patch({ redirectUrl: e.target.value || undefined })}
          placeholder="https://…"
          maxLength={2000}
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor={`${uid}-captcha`}>Captcha provider</Label>
        <Select
          value={captchaProvider}
          onValueChange={(v) => {
            const next = v as 'none' | 'jotform' | 'recaptcha';
            patch({
              captchaProvider: next === 'none' ? undefined : next,
              ...(next !== 'recaptcha' ? { recaptchaSiteKey: undefined } : {}),
            });
          }}
        >
          <SelectTrigger id={`${uid}-captcha`} className="bg-background">
            <SelectValue placeholder="None" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            <SelectItem value="jotform">JotForm</SelectItem>
            <SelectItem value="recaptcha">reCAPTCHA</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {captchaProvider === 'recaptcha' ? (
        <div className="grid gap-2">
          <Label htmlFor={`${uid}-recaptcha-key`}>reCAPTCHA site key</Label>
          <Input
            id={`${uid}-recaptcha-key`}
            value={recaptchaSiteKey}
            onChange={(e) => patch({ recaptchaSiteKey: e.target.value || undefined })}
            placeholder="Public site key for the client"
            className="font-mono text-xs"
            maxLength={500}
          />
        </div>
      ) : null}
    </div>
  );
}

function FormOptionsSidebarContent({
  formDescription,
  onFormDescriptionChange,
  formSettings,
  onFormSettingsChange,
}: {
  formDescription: string;
  onFormDescriptionChange: (v: string) => void;
  formSettings: Record<string, unknown>;
  onFormSettingsChange: (next: Record<string, unknown>) => void;
}) {
  const uid = useId();
  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor={`${uid}-form-desc`} className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Form description
        </Label>
        <p className="text-[11px] text-muted-foreground">Optional. Shown to respondents if your theme supports it.</p>
        <Textarea
          id={`${uid}-form-desc`}
          className="min-h-[72px] resize-none text-sm"
          placeholder="Brief summary or instructions for this form"
          value={formDescription}
          onChange={(e) => onFormDescriptionChange(e.target.value)}
          maxLength={2000}
        />
      </div>
      <Separator />
      <FormSettingsFields settings={formSettings} onChange={onFormSettingsChange} />
    </div>
  );
}

function FieldPropertiesInspector({
  fields,
  selectedId,
  onUpdateField,
  onDeleteField,
  pendingInspectorTab,
  onPendingInspectorTabConsumed,
}: {
  fields: FormField[];
  selectedId: string | null;
  onUpdateField: (id: string, patch: Partial<FormField>) => void;
  onDeleteField: (id: string) => void;
  pendingInspectorTab?: PublishInspectorTab | null;
  onPendingInspectorTabConsumed?: () => void;
}) {
  const uid = useId();
  const [propsTab, setPropsTab] = useState<string>('basics');
  const prevSelectedRef = useRef<string | null>(null);
  const [overrideDateKey, setOverrideDateKey] = useState<string>('');
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  useEffect(() => {
    setOverrideDateKey('');
  }, [selectedId]);

  useLayoutEffect(() => {
    if (!selectedId) {
      setPropsTab('basics');
      prevSelectedRef.current = null;
      return;
    }
    if (pendingInspectorTab) {
      setPropsTab(pendingInspectorTab);
      onPendingInspectorTabConsumed?.();
      prevSelectedRef.current = selectedId;
      return;
    }
    if (prevSelectedRef.current !== selectedId) {
      setPropsTab('basics');
    }
    prevSelectedRef.current = selectedId;
  }, [selectedId, pendingInspectorTab, onPendingInspectorTabConsumed]);

  // Hooks must run unconditionally; keep memoized helpers above early-returns.
  const fieldById = useMemo(() => new Map(fields.map((f) => [f.id, f] as const)), [fields]);
  const fieldLabel = (id: string) => {
    const f = fieldById.get(id);
    const label = f?.label;
    const uniqueName = (f as any)?.uniqueName;
    if (typeof label === 'string' && label.trim()) return label;
    if (typeof uniqueName === 'string' && uniqueName.trim()) return uniqueName;
    return id;
  };
  const getFieldChoiceOptions = (fieldId: string): FieldOption[] => {
    const f = fieldById.get(fieldId);
    return f ? getChoiceOptions(f) : [];
  };

  const squarePaymentFields = useMemo(() => listSquarePaymentFields(fields), [fields]);
  const rescheduleCrossFieldWarning = useMemo(() => {
    const charging = fields.filter((f) => {
      if (f.type !== 'appointment') return false;
      const v = (f.config as Record<string, unknown> | undefined)?.rescheduleFeeUsd;
      return typeof v === 'number' && v > 0;
    });
    if (charging.length < 2) return null;
    const ids = new Set(
      charging
        .map((f) => (f.config as Record<string, unknown>)?.reschedulePaymentFieldId)
        .filter((x): x is string => typeof x === 'string' && x.length > 0)
    );
    if (ids.size <= 1) return null;
    return 'Multiple appointment fields have a reschedule fee but use different Square payment fields. Public submit may fail (RESCHEDULE_PAYMENT_FIELD_CONFLICT). Use the same payment field for all.';
  }, [fields]);

  const selected = selectedId ? fields.find((f) => f.id === selectedId) : undefined;
  if (!selected) {
    return (
      <p className="px-0 py-1 text-sm text-muted-foreground">
        Select a field on the canvas to edit its properties.
      </p>
    );
  }

  const supportsPlaceholderHint = ![
    'divider',
    'pageBreak',
    'header',
    'paragraph',
    'submit',
    'payment',
  ].includes(selected.type);

  /** Layout/static blocks never get list columns; hide the admin table toggle (matches API). */
  const supportsSubmissionsTableToggle = ![
    'header',
    'paragraph',
    'richText',
    'divider',
    'pageBreak',
    'submit',
  ].includes(selected.type);

  const qpGeneral = selected.questionProperties?.general ?? {};
  const qpAdvanced = (selected.questionProperties?.advanced ?? {}) as Record<string, unknown>;

  const patchQuestionPropertiesGeneral = (patch: Record<string, unknown>) => {
    onUpdateField(selected.id, {
      questionProperties: {
        ...selected.questionProperties,
        general: {
          ...qpGeneral,
          ...patch,
        },
      },
    });
  };

  const patchQuestionPropertiesAdvanced = (patch: Record<string, unknown>) => {
    const next = { ...qpAdvanced, ...patch };
    for (const k of Object.keys(patch)) {
      if (patch[k] === undefined) delete next[k];
    }
    onUpdateField(selected.id, {
      questionProperties: {
        ...selected.questionProperties,
        advanced: next,
      },
    });
  };

  const validationObj = (selected.validation ?? {}) as Record<string, unknown>;
  const patchValidation = (patch: Record<string, unknown>) => {
    const next = { ...validationObj, ...patch };
    for (const k of Object.keys(patch)) {
      if (patch[k] === undefined) delete next[k];
    }
    onUpdateField(selected.id, { validation: next });
  };

  const configObj = (selected.config ?? {}) as Record<string, unknown>;
  const patchConfig = (patch: Record<string, unknown>) => {
    const next = { ...configObj, ...patch };
    for (const k of Object.keys(patch)) {
      if (patch[k] === undefined) delete next[k];
    }
    onUpdateField(selected.id, { config: next });
  };

  const logicArr = Array.isArray(selected.logic) ? selected.logic : [];
  const otherFieldIds = fields.map((f) => f.id).filter(Boolean).filter((id) => id !== selected.id);

  const coerceBlocks = (arr: unknown[]): FieldConditionalLogicBlock[] => {
    return arr
      .map((b) => (isPlainObject(b) ? (b as Record<string, unknown>) : null))
      .filter((b): b is Record<string, unknown> => Boolean(b))
      .map((b) => {
        const action = typeof b.action === 'string' ? (b.action as FieldLogicAction) : 'show';
        const match = b.match === 'any' ? 'any' : 'all';
        const whenRaw = Array.isArray(b.when) ? (b.when as unknown[]) : [];
        const when = whenRaw
          .map((w) => (isPlainObject(w) ? (w as Record<string, unknown>) : null))
          .filter((w): w is Record<string, unknown> => Boolean(w))
          .map((w) => {
            const fieldId = typeof w.fieldId === 'string' ? w.fieldId : '';
            const op: 'eq' | 'in' = w.op === 'in' ? 'in' : 'eq';
            const value = typeof w.value === 'string' ? w.value : undefined;
            const values = Array.isArray(w.values) ? (w.values as unknown[]).map(String) : undefined;
            return { fieldId, op, value, values };
          });
        return { action, match, when } satisfies FieldConditionalLogicBlock;
      });
  };

  const blocks = coerceBlocks(logicArr);
  const setBlocks = (next: FieldConditionalLogicBlock[]) => {
    onUpdateField(selected.id, { logic: next as unknown as any[] });
  };

  return (
    <Tabs value={propsTab} onValueChange={setPropsTab} className="flex flex-col gap-3">
      <TabsList className="grid w-full grid-cols-5">
        <TabsTrigger value="basics">Basics</TabsTrigger>
        <TabsTrigger value="layout">Layout</TabsTrigger>
        <TabsTrigger value="validation">Validation</TabsTrigger>
        <TabsTrigger value="config">Config</TabsTrigger>
        <TabsTrigger value="logic">Logic</TabsTrigger>
      </TabsList>

      <TabsContent value="basics" className="space-y-4 data-[state=inactive]:hidden">
        {selected.type !== 'divider' && selected.type !== 'pageBreak' && selected.type !== 'submit' && (
          <div className="grid gap-2">
            <Label htmlFor={`${uid}-label`}>Label</Label>
            <Input
              id={`${uid}-label`}
              value={selected.label ?? ''}
              onChange={(e) => onUpdateField(selected.id, { label: e.target.value })}
              maxLength={500}
            />
          </div>
        )}
        {selected.type === 'paragraph' && (
          <div className="grid gap-2">
            <Label htmlFor={`${uid}-html`}>HTML</Label>
            <Textarea
              id={`${uid}-html`}
              rows={4}
              value={selected.html ?? ''}
              onChange={(e) => onUpdateField(selected.id, { html: e.target.value })}
              className="font-mono text-xs"
            />
          </div>
        )}
        {selected.type === 'richText' && (
          <div className="grid gap-2">
            <Label>Content</Label>
            <div className="rounded-xl border border-border/60 bg-muted/10 p-2">
              <RichTextEditor
                value={selected.html ?? ''}
                onChange={(value) => onUpdateField(selected.id, { html: value })}
                minHeight={260}
                maxHeight={520}
                showBorder={false}
                enableAI={false}
                title={selected.label ?? 'Rich text'}
              />
            </div>
            <p className="text-[11px] leading-snug text-muted-foreground">
              This is a content block (not a question). Use it for Terms &amp; Conditions, instructions, or formatted sections.
            </p>
          </div>
        )}
        {supportsPlaceholderHint && (
          <>
            <div className="grid gap-2">
              <Label htmlFor={`${uid}-ph`}>Placeholder</Label>
              <Input
                id={`${uid}-ph`}
                value={selected.placeholder ?? ''}
                onChange={(e) => onUpdateField(selected.id, { placeholder: e.target.value })}
                maxLength={500}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor={`${uid}-hint`}>Hint</Label>
              <Input
                id={`${uid}-hint`}
                value={selected.hint ?? ''}
                onChange={(e) => onUpdateField(selected.id, { hint: e.target.value })}
                maxLength={2000}
              />
            </div>
          </>
        )}

        <Button
          type="button"
          variant="destructive"
          size="sm"
          className="w-full gap-2"
          onClick={() => onDeleteField(selected.id)}
        >
          <Trash2 className="h-4 w-4" />
          Remove field
        </Button>
      </TabsContent>

      <TabsContent value="layout" className="space-y-4 data-[state=inactive]:hidden">
        <div className="grid gap-3 rounded-xl border border-border/60 bg-muted/10 p-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Question properties</p>
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-3 py-2">
              <Label htmlFor={`${uid}-req`} className="text-sm font-normal">
                Required
              </Label>
              <Switch
                id={`${uid}-req`}
                checked={Boolean(qpGeneral.required)}
                onCheckedChange={(checked) => patchQuestionPropertiesGeneral({ required: checked })}
              />
            </div>
            <div className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-3 py-2">
              <Label htmlFor={`${uid}-lblvis`} className="text-sm font-normal">
                Label visible
              </Label>
              <Switch
                id={`${uid}-lblvis`}
                checked={qpGeneral.labelVisible !== false}
                onCheckedChange={(checked) =>
                  patchQuestionPropertiesGeneral({ labelVisible: checked ? undefined : false })
                }
              />
            </div>
          </div>

          {supportsSubmissionsTableToggle ? (
            <div className="rounded-lg border border-border/60 bg-background px-3 py-2">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor={`${uid}-submissions-col`} className="text-sm font-normal">
                  Show in submissions table
                </Label>
                <Switch
                  id={`${uid}-submissions-col`}
                  checked={qpGeneral.showInSubmissionsTable === true}
                  onCheckedChange={(checked) =>
                    patchQuestionPropertiesGeneral({ showInSubmissionsTable: checked })
                  }
                />
              </div>
              <p className="mt-1.5 text-[11px] leading-snug text-muted-foreground">
                Admin list only. Open a row to see every answer; CSV export still includes all columns.
              </p>
            </div>
          ) : null}

          <div className="grid gap-2 sm:grid-cols-2">
            <div className="grid gap-1">
              <Label>Label align</Label>
              <Select
                value={(typeof qpGeneral.labelAlign === 'string' ? qpGeneral.labelAlign : 'top') as string}
                onValueChange={(v) => patchQuestionPropertiesGeneral({ labelAlign: v })}
              >
                <SelectTrigger className="bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="top">Top</SelectItem>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1">
              <Label>Content align</Label>
              <Select
                value={(typeof qpGeneral.align === 'string' ? qpGeneral.align : 'left') as string}
                onValueChange={(v) => patchQuestionPropertiesGeneral({ align: v })}
              >
                <SelectTrigger className="bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                  <SelectItem value="top">Top</SelectItem>
                  <SelectItem value="bottom">Bottom</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            <div className="grid gap-1">
              <Label>Width</Label>
              <Input
                value={typeof qpGeneral.width === 'string' ? qpGeneral.width : ''}
                onChange={(e) => patchQuestionPropertiesGeneral({ width: e.target.value || undefined })}
                placeholder="e.g. 100%, 320px"
              />
            </div>
            <div className="grid gap-1">
              <Label>Height</Label>
              <Input
                value={typeof (qpGeneral as any).height === 'string' ? ((qpGeneral as any).height as string) : ''}
                onChange={(e) => patchQuestionPropertiesGeneral({ height: e.target.value || undefined } as any)}
                placeholder="e.g. auto, 48px"
              />
            </div>
          </div>
        </div>

        <div className="grid gap-3 rounded-xl border border-border/60 bg-muted/10 p-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Advanced</p>
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-3 py-2">
              <Label className="text-sm font-normal">Hide field</Label>
              <Switch
                checked={qpAdvanced.hideField === true}
                onCheckedChange={(checked) => patchQuestionPropertiesAdvanced({ hideField: checked ? true : undefined })}
              />
            </div>
            <div className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-3 py-2">
              <Label className="text-sm font-normal">Move to new line</Label>
              <Switch
                checked={qpAdvanced.moveToNewLine === true}
                onCheckedChange={(checked) =>
                  patchQuestionPropertiesAdvanced({ moveToNewLine: checked ? true : undefined })
                }
              />
            </div>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-3 py-2">
              <Label className="text-sm font-normal">Shrink</Label>
              <Switch
                checked={qpAdvanced.shrink === true}
                onCheckedChange={(checked) => patchQuestionPropertiesAdvanced({ shrink: checked ? true : undefined })}
              />
            </div>
            <div className="grid gap-1">
              <Label>Unique name</Label>
              <Input
                value={typeof qpAdvanced.uniqueName === 'string' ? (qpAdvanced.uniqueName as string) : ''}
                onChange={(e) => patchQuestionPropertiesAdvanced({ uniqueName: e.target.value || undefined })}
                placeholder="Optional integration alias"
              />
            </div>
          </div>

        </div>
      </TabsContent>

      <TabsContent value="validation" className="space-y-4 data-[state=inactive]:hidden">
        <div className="grid gap-3 rounded-xl border border-border/60 bg-muted/10 p-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Validation</p>

          {(selected.type === 'shortText' || selected.type === 'longText') && (
            <div className="grid grid-cols-2 gap-2">
              <div className="grid gap-1">
                <Label>Min length</Label>
                <Input
                  type="number"
                  min={0}
                  placeholder="—"
                  value={typeof validationObj.minLength === 'number' ? (validationObj.minLength as number) : ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    patchValidation({ minLength: raw === '' ? undefined : Math.max(0, Number.parseInt(raw, 10) || 0) });
                  }}
                />
              </div>
              <div className="grid gap-1">
                <Label>Max length</Label>
                <Input
                  type="number"
                  min={0}
                  placeholder="—"
                  value={typeof validationObj.maxLength === 'number' ? (validationObj.maxLength as number) : ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    patchValidation({ maxLength: raw === '' ? undefined : Math.max(0, Number.parseInt(raw, 10) || 0) });
                  }}
                />
              </div>
            </div>
          )}

          {selected.type === 'number' && (
            <div className="grid grid-cols-2 gap-2">
              <div className="grid gap-1">
                <Label>Min</Label>
                <Input
                  type="number"
                  placeholder="—"
                  value={typeof validationObj.min === 'number' ? (validationObj.min as number) : ''}
                  onChange={(e) => patchValidation({ min: e.target.value === '' ? undefined : Number(e.target.value) })}
                />
              </div>
              <div className="grid gap-1">
                <Label>Max</Label>
                <Input
                  type="number"
                  placeholder="—"
                  value={typeof validationObj.max === 'number' ? (validationObj.max as number) : ''}
                  onChange={(e) => patchValidation({ max: e.target.value === '' ? undefined : Number(e.target.value) })}
                />
              </div>
            </div>
          )}

          {(selected.type === 'number' || selected.type === 'spinner') && (
            <div className="grid grid-cols-2 gap-2">
              <div className="grid gap-1">
                <Label>Step</Label>
                <Input
                  type="number"
                  placeholder="—"
                  value={typeof validationObj.step === 'number' ? (validationObj.step as number) : ''}
                  onChange={(e) => patchValidation({ step: e.target.value === '' ? undefined : Number(e.target.value) })}
                />
              </div>
              <div className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-3 py-2">
                <Label className="text-sm font-normal">Integer only</Label>
                <Switch
                  checked={validationObj.integerOnly === true}
                  onCheckedChange={(checked) => patchValidation({ integerOnly: checked ? true : undefined })}
                />
              </div>
            </div>
          )}

          {selected.type === 'multipleChoice' && (
            <div className="grid grid-cols-2 gap-2">
              <div className="grid gap-1">
                <Label>Min selections</Label>
                <Input
                  type="number"
                  min={0}
                  placeholder="—"
                  value={getMinSelections(selected) ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    patchValidation({
                      minSelections: raw === '' ? undefined : Math.max(0, Number.parseInt(raw, 10) || 0),
                    });
                  }}
                />
              </div>
              <div className="grid gap-1">
                <Label>Max selections</Label>
                <Input
                  type="number"
                  min={0}
                  placeholder="—"
                  value={getMaxSelections(selected) ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    patchValidation({
                      maxSelections: raw === '' ? undefined : Math.max(0, Number.parseInt(raw, 10) || 0),
                    });
                  }}
                />
              </div>
            </div>
          )}

          {(selected.type === 'shortText' || selected.type === 'longText' || selected.type === 'email' || selected.type === 'phone') && (
            <div className="grid gap-2">
              <Label>Pattern (regex)</Label>
              <Input
                value={typeof validationObj.pattern === 'string' ? (validationObj.pattern as string) : ''}
                onChange={(e) => patchValidation({ pattern: e.target.value || undefined })}
                placeholder="e.g. ^[A-Za-z]+$"
                className="font-mono text-xs"
              />
            </div>
          )}

          {selected.type === 'datePicker' && (
            <div className="grid grid-cols-2 gap-2">
              <div className="grid gap-1">
                <Label>Min date</Label>
                <Input
                  value={typeof validationObj.minDate === 'string' ? (validationObj.minDate as string) : ''}
                  onChange={(e) => patchValidation({ minDate: e.target.value || undefined })}
                  placeholder="YYYY-MM-DD"
                  className="font-mono text-xs"
                />
              </div>
              <div className="grid gap-1">
                <Label>Max date</Label>
                <Input
                  value={typeof validationObj.maxDate === 'string' ? (validationObj.maxDate as string) : ''}
                  onChange={(e) => patchValidation({ maxDate: e.target.value || undefined })}
                  placeholder="YYYY-MM-DD"
                  className="font-mono text-xs"
                />
              </div>
            </div>
          )}

          {selected.type === 'time' && (
            <div className="grid grid-cols-2 gap-2">
              <div className="grid gap-1">
                <Label>Min time</Label>
                <Input
                  value={typeof validationObj.minTime === 'string' ? (validationObj.minTime as string) : ''}
                  onChange={(e) => patchValidation({ minTime: e.target.value || undefined })}
                  placeholder="HH:mm"
                  className="font-mono text-xs"
                />
              </div>
              <div className="grid gap-1">
                <Label>Max time</Label>
                <Input
                  value={typeof validationObj.maxTime === 'string' ? (validationObj.maxTime as string) : ''}
                  onChange={(e) => patchValidation({ maxTime: e.target.value || undefined })}
                  placeholder="HH:mm"
                  className="font-mono text-xs"
                />
              </div>
            </div>
          )}

          {selected.type === 'fileUpload' && (
            <div className="grid gap-2">
              <div className="grid grid-cols-2 gap-2">
                <div className="grid gap-1">
                  <Label>Max files</Label>
                  <Input
                    type="number"
                    min={0}
                    placeholder="—"
                    value={typeof validationObj.maxFiles === 'number' ? (validationObj.maxFiles as number) : ''}
                    onChange={(e) => {
                      const raw = e.target.value;
                      patchValidation({ maxFiles: raw === '' ? undefined : Math.max(0, Number.parseInt(raw, 10) || 0) });
                    }}
                  />
                </div>
                <div className="grid gap-1">
                  <Label>Max file size (bytes)</Label>
                  <Input
                    type="number"
                    min={0}
                    placeholder="—"
                    value={typeof validationObj.maxFileSizeBytes === 'number' ? (validationObj.maxFileSizeBytes as number) : ''}
                    onChange={(e) => {
                      const raw = e.target.value;
                      patchValidation({ maxFileSizeBytes: raw === '' ? undefined : Math.max(0, Number.parseInt(raw, 10) || 0) });
                    }}
                  />
                </div>
              </div>
              <div className="grid gap-1">
                <Label>Accept extensions (comma-separated)</Label>
                <Input
                  value={Array.isArray(validationObj.acceptExtensions) ? (validationObj.acceptExtensions as any[]).join(', ') : ''}
                  onChange={(e) => {
                    const raw = e.target.value.trim();
                    patchValidation({
                      acceptExtensions:
                        raw === '' ? undefined : raw.split(',').map((s) => s.trim()).filter(Boolean),
                    });
                  }}
                  placeholder=".pdf, .png, .jpg"
                  className="font-mono text-xs"
                />
              </div>
              <div className="grid gap-1">
                <Label>Accept mime types (comma-separated)</Label>
                <Input
                  value={Array.isArray(validationObj.acceptMimeTypes) ? (validationObj.acceptMimeTypes as any[]).join(', ') : ''}
                  onChange={(e) => {
                    const raw = e.target.value.trim();
                    patchValidation({
                      acceptMimeTypes:
                        raw === '' ? undefined : raw.split(',').map((s) => s.trim()).filter(Boolean),
                    });
                  }}
                  placeholder="application/pdf, image/*"
                  className="font-mono text-xs"
                />
              </div>
            </div>
          )}

        </div>
      </TabsContent>

      <TabsContent value="config" className="space-y-4 data-[state=inactive]:hidden">
        {['dropdown', 'singleChoice', 'multipleChoice'].includes(selected.type) && (
          <ChoiceOptionsEditor field={selected} allFields={fields} onUpdateField={onUpdateField} />
        )}

        {selected.type === 'email' && (
          <div className="grid gap-3 rounded-xl border border-border/60 bg-muted/10 p-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email config</p>
            <div className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-3 py-2">
              <Label className="text-sm font-normal">Confirm email</Label>
              <Switch
                checked={configObj.confirmEmail === true}
                onCheckedChange={(checked) => patchConfig({ confirmEmail: checked ? true : undefined })}
              />
            </div>
            {configObj.confirmEmail === true ? (
              <div className="grid gap-1">
                <Label>Confirm matches field id (optional)</Label>
                <Select
                  value={
                    typeof configObj.confirmMatchesFieldId === 'string'
                      ? (configObj.confirmMatchesFieldId as string)
                      : '__auto__'
                  }
                  onValueChange={(v) =>
                    patchConfig({ confirmMatchesFieldId: v === '__auto__' ? undefined : v })
                  }
                >
                  <SelectTrigger className="bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__auto__">Auto (`&lt;id&gt;_confirm`)</SelectItem>
                    {otherFieldIds.map((id) => (
                      <SelectItem key={id} value={id}>
                        {id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">
                  If omitted, submit expects <span className="font-mono">{selected.id}_confirm</span>.
                </p>
              </div>
            ) : null}
            <div className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-3 py-2">
              <Label className="text-sm font-normal">Send verification</Label>
              <Switch
                checked={configObj.sendVerification === true}
                onCheckedChange={(checked) => patchConfig({ sendVerification: checked ? true : undefined })}
              />
            </div>
          </div>
        )}

        {selected.type === 'fullName' && (
          <div className="grid gap-3 rounded-xl border border-border/60 bg-muted/10 p-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Full name config</p>
            <div className="grid gap-2 sm:grid-cols-3">
              <div className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-3 py-2">
                <Label className="text-sm font-normal">Prefix</Label>
                <Switch
                  checked={configObj.showPrefix === true}
                  onCheckedChange={(checked) => patchConfig({ showPrefix: checked ? true : undefined })}
                />
              </div>
              <div className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-3 py-2">
                <Label className="text-sm font-normal">Middle</Label>
                <Switch
                  checked={configObj.showMiddle === true}
                  onCheckedChange={(checked) => patchConfig({ showMiddle: checked ? true : undefined })}
                />
              </div>
              <div className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-3 py-2">
                <Label className="text-sm font-normal">Suffix</Label>
                <Switch
                  checked={configObj.showSuffix === true}
                  onCheckedChange={(checked) => patchConfig({ showSuffix: checked ? true : undefined })}
                />
              </div>
            </div>
          </div>
        )}

        {selected.type === 'address' && (
          <div className="grid gap-3 rounded-xl border border-border/60 bg-muted/10 p-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Address config</p>
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="grid gap-1">
                <Label>Layout</Label>
                <Select
                  value={typeof configObj.layout === 'string' ? (configObj.layout as string) : 'us'}
                  onValueChange={(v) => patchConfig({ layout: v })}
                >
                  <SelectTrigger className="bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="us">US</SelectItem>
                    <SelectItem value="international">International</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1">
                <Label>Subfield ids</Label>
                <Input
                  value={Array.isArray(configObj.subfieldIds) ? (configObj.subfieldIds as any[]).join(', ') : ''}
                  onChange={(e) =>
                    patchConfig({
                      subfieldIds:
                        e.target.value.trim() === ''
                          ? undefined
                          : e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                    })
                  }
                  placeholder="street, city, zip, country…"
                  className="font-mono text-xs"
                />
              </div>
            </div>
          </div>
        )}

        {selected.type === 'phone' && (
          <div className="grid gap-3 rounded-xl border border-border/60 bg-muted/10 p-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Phone config</p>
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="grid gap-1">
                <Label>Default country code</Label>
                <Input
                  value={typeof configObj.defaultCountryCode === 'string' ? (configObj.defaultCountryCode as string) : ''}
                  onChange={(e) => patchConfig({ defaultCountryCode: e.target.value || undefined })}
                  placeholder="e.g. +1"
                  className="font-mono text-xs"
                />
              </div>
              <div className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-3 py-2">
                <Label className="text-sm font-normal">Allow extension</Label>
                <Switch
                  checked={configObj.allowExtension === true}
                  onCheckedChange={(checked) => patchConfig({ allowExtension: checked ? true : undefined })}
                />
              </div>
            </div>
          </div>
        )}

        {selected.type === 'datePicker' && (
          <div className="grid gap-3 rounded-xl border border-border/60 bg-muted/10 p-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Date picker config</p>
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-3 py-2">
                <Label className="text-sm font-normal">Include time</Label>
                <Switch
                  checked={configObj.includeTime === true}
                  onCheckedChange={(checked) => patchConfig({ includeTime: checked ? true : undefined })}
                />
              </div>
              <div className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-3 py-2">
                <Label className="text-sm font-normal">Use native picker</Label>
                <Switch
                  checked={configObj.useNativePicker === true}
                  onCheckedChange={(checked) => patchConfig({ useNativePicker: checked ? true : undefined })}
                />
              </div>
            </div>
            <div className="grid gap-1">
              <Label>Date format</Label>
              <Input
                value={typeof configObj.dateFormat === 'string' ? (configObj.dateFormat as string) : ''}
                onChange={(e) => patchConfig({ dateFormat: e.target.value || undefined })}
                placeholder="e.g. YYYY-MM-DD"
                className="font-mono text-xs"
              />
            </div>
          </div>
        )}

        {selected.type === 'captcha' && (
          <div className="grid gap-3 rounded-xl border border-border/60 bg-muted/10 p-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Captcha config</p>
            <div className="grid gap-1">
              <Label>Provider</Label>
              <Select
                value={typeof configObj.provider === 'string' ? (configObj.provider as string) : 'default'}
                onValueChange={(v) => patchConfig({ provider: v })}
              >
                <SelectTrigger className="bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default</SelectItem>
                  <SelectItem value="recaptcha">reCAPTCHA</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {selected.type === 'signature' && (
          <div className="grid gap-3 rounded-xl border border-border/60 bg-muted/10 p-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Signature config</p>
            <div className="grid grid-cols-2 gap-2">
              <div className="grid gap-1">
                <Label>Canvas width</Label>
                <Input
                  type="number"
                  min={50}
                  placeholder="—"
                  value={typeof configObj.canvasWidth === 'number' ? (configObj.canvasWidth as number) : ''}
                  onChange={(e) =>
                    patchConfig({ canvasWidth: e.target.value === '' ? undefined : Number(e.target.value) })
                  }
                />
              </div>
              <div className="grid gap-1">
                <Label>Canvas height</Label>
                <Input
                  type="number"
                  min={50}
                  placeholder="—"
                  value={typeof configObj.canvasHeight === 'number' ? (configObj.canvasHeight as number) : ''}
                  onChange={(e) =>
                    patchConfig({ canvasHeight: e.target.value === '' ? undefined : Number(e.target.value) })
                  }
                />
              </div>
            </div>
            <div className="grid gap-1">
              <Label>Pen color</Label>
              <Input
                value={typeof configObj.penColor === 'string' ? (configObj.penColor as string) : ''}
                onChange={(e) => patchConfig({ penColor: e.target.value || undefined })}
                placeholder="#000000"
                className="font-mono text-xs"
              />
            </div>
            <div className="grid gap-1">
              <Label>Legal disclaimer</Label>
              <Textarea
                rows={3}
                value={typeof configObj.legalDisclaimer === 'string' ? (configObj.legalDisclaimer as string) : ''}
                onChange={(e) => patchConfig({ legalDisclaimer: e.target.value || undefined })}
              />
            </div>
          </div>
        )}

        {selected.type === 'payment' && (
          <div className="grid gap-3 rounded-xl border border-border/60 bg-muted/10 p-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Payment (Square)</p>
            <p className="text-[11px] text-muted-foreground">
              Used for product totals or as the Square payment target for reschedule charges. The reschedule dollar amount is set on the appointment field.
            </p>
            <div className="grid gap-1">
              <Label>Gateway</Label>
              <Select
                value={typeof configObj.gateway === 'string' ? String(configObj.gateway) : 'square'}
                onValueChange={(v) => patchConfig({ gateway: v })}
              >
                <SelectTrigger className="bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="square">Square</SelectItem>
                  <SelectItem value="stripe">Stripe</SelectItem>
                  <SelectItem value="paypal">PayPal</SelectItem>
                  <SelectItem value="authorizeNet">Authorize.net</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1">
              <Label>Amount source</Label>
              <Select
                value={typeof configObj.amountSource === 'string' ? String(configObj.amountSource) : 'fixed'}
                onValueChange={(v) => patchConfig({ amountSource: v })}
              >
                <SelectTrigger className="bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Fixed amount (USD)</SelectItem>
                  <SelectItem value="productList">Product list total</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {typeof configObj.amountSource === 'string' && configObj.amountSource === 'productList' ? (
              <div className="grid gap-1">
                <Label htmlFor="product-list-field-id">Product list field id</Label>
                <Input
                  id="product-list-field-id"
                  className="bg-background font-mono text-xs"
                  placeholder="id of a productList field on this form"
                  value={typeof configObj.productListFieldId === 'string' ? configObj.productListFieldId : ''}
                  onChange={(e) => patchConfig({ productListFieldId: e.target.value || undefined })}
                />
              </div>
            ) : (
              <div className="grid gap-1">
                <Label htmlFor="payment-fixed-amount">Fixed amount (USD)</Label>
                <Input
                  id="payment-fixed-amount"
                  type="number"
                  min={0}
                  step="0.01"
                  className="bg-background"
                  value={
                    typeof configObj.amount === 'number' && Number.isFinite(configObj.amount) ? configObj.amount : ''
                  }
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      patchConfig({ amount: undefined });
                      return;
                    }
                    const n = Number.parseFloat(raw);
                    if (!Number.isFinite(n)) return;
                    patchConfig({ amount: Math.max(0, n) });
                  }}
                />
                <p className="text-[11px] text-muted-foreground">
                  Use <span className="font-medium">0</span> when this field only exists for reschedule charges (fee is set on the appointment field).
                </p>
              </div>
            )}
          </div>
        )}

        {selected.type === 'appointment' && (
          <div className="grid gap-3">
            <AppointmentConfigEditor
              config={configObj}
              onPatchConfig={(patch) => patchConfig(patch)}
              squarePaymentFields={squarePaymentFields}
              crossFieldRescheduleWarning={rescheduleCrossFieldWarning}
            />
            {false && (
          <div className="grid gap-3 rounded-xl border border-border/60 bg-muted/10 p-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Appointment config</p>
            <div className="grid grid-cols-2 gap-2">
              <div className="grid gap-1">
                <Label>Slot duration (minutes)</Label>
                <Input
                  type="number"
                  min={5}
                  max={1440}
                  value={typeof configObj.slotDurationMinutes === 'number' ? (configObj.slotDurationMinutes as number) : ''}
                  onChange={(e) =>
                    patchConfig({
                      slotDurationMinutes:
                        e.target.value === ''
                          ? undefined
                          : Math.max(5, Math.min(1440, Number.parseInt(e.target.value, 10) || 30)),
                    })
                  }
                />
              </div>
              <div className="grid gap-1">
                <Label>Slot interval (minutes)</Label>
                <Input
                  type="number"
                  min={5}
                  max={1440}
                  value={typeof configObj.slotIntervalMinutes === 'number' ? (configObj.slotIntervalMinutes as number) : ''}
                  onChange={(e) =>
                    patchConfig({
                      slotIntervalMinutes:
                        e.target.value === ''
                          ? undefined
                          : Math.max(5, Math.min(1440, Number.parseInt(e.target.value, 10) || 30)),
                    })
                  }
                />
              </div>
              <div className="grid gap-1">
                <Label>Buffer before (minutes)</Label>
                <Input
                  type="number"
                  min={0}
                  max={240}
                  value={typeof configObj.bufferBeforeMinutes === 'number' ? (configObj.bufferBeforeMinutes as number) : ''}
                  onChange={(e) =>
                    patchConfig({
                      bufferBeforeMinutes:
                        e.target.value === ''
                          ? undefined
                          : Math.max(0, Math.min(240, Number.parseInt(e.target.value, 10) || 0)),
                    })
                  }
                />
              </div>
              <div className="grid gap-1">
                <Label>Buffer after (minutes)</Label>
                <Input
                  type="number"
                  min={0}
                  max={240}
                  value={typeof configObj.bufferAfterMinutes === 'number' ? (configObj.bufferAfterMinutes as number) : ''}
                  onChange={(e) =>
                    patchConfig({
                      bufferAfterMinutes:
                        e.target.value === ''
                          ? undefined
                          : Math.max(0, Math.min(240, Number.parseInt(e.target.value, 10) || 0)),
                    })
                  }
                />
              </div>
            </div>

            <div className="grid gap-2 rounded-lg border border-border/60 bg-background p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Booking window</p>
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="grid gap-1">
                  <Label>Mode</Label>
                  <Select
                    value={
                      isPlainObject(configObj.bookingWindow) && typeof (configObj.bookingWindow as any).mode === 'string'
                        ? String((configObj.bookingWindow as any).mode)
                        : 'rolling'
                    }
                    onValueChange={(v) => {
                      const prev = isPlainObject(configObj.bookingWindow)
                        ? (configObj.bookingWindow as Record<string, unknown>)
                        : {};
                      patchConfig({ bookingWindow: { ...prev, mode: v } });
                    }}
                  >
                    <SelectTrigger className="bg-background">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rolling">Rolling</SelectItem>
                      <SelectItem value="absolute">Absolute</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-1">
                  <Label>Rolling days ahead</Label>
                  <Input
                    type="number"
                    min={1}
                    max={730}
                    placeholder="e.g. 30"
                    value={
                      isPlainObject(configObj.bookingWindow) &&
                      typeof (configObj.bookingWindow as any).rollingDaysAhead === 'number'
                        ? Number((configObj.bookingWindow as any).rollingDaysAhead)
                        : ''
                    }
                    onChange={(e) => {
                      const prev = isPlainObject(configObj.bookingWindow)
                        ? (configObj.bookingWindow as Record<string, unknown>)
                        : {};
                      patchConfig({
                        bookingWindow: {
                          ...prev,
                          rollingDaysAhead:
                            e.target.value === ''
                              ? undefined
                              : Math.max(1, Math.min(730, Number.parseInt(e.target.value, 10) || 30)),
                        },
                      });
                    }}
                  />
                </div>
                <div className="grid gap-1">
                  <Label>Start date (absolute)</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className="justify-start text-left font-normal"
                        data-empty={
                          !(
                            isPlainObject(configObj.bookingWindow) &&
                            typeof (configObj.bookingWindow as any).startDate === 'string' &&
                            String((configObj.bookingWindow as any).startDate)
                          )
                        }
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {(() => {
                          const d = parseDateOnly(
                            isPlainObject(configObj.bookingWindow) ? (configObj.bookingWindow as any).startDate : undefined
                          );
                          return d ? format(d as Date, 'PPP') : 'Pick start date';
                        })()}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        captionLayout="dropdown"
                        disabled={{ before: today }}
                        fromMonth={today}
                        fromYear={today.getFullYear()}
                        selected={parseDateOnly(
                          isPlainObject(configObj.bookingWindow) ? (configObj.bookingWindow as any).startDate : undefined
                        )}
                        onSelect={(d) => {
                          const prev = isPlainObject(configObj.bookingWindow)
                            ? (configObj.bookingWindow as Record<string, unknown>)
                            : {};
                          patchConfig({ bookingWindow: { ...prev, startDate: d ? toDateOnlyString(d) : undefined } });
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="grid gap-1">
                  <Label>End date (absolute)</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className="justify-start text-left font-normal"
                        data-empty={
                          !(
                            isPlainObject(configObj.bookingWindow) &&
                            typeof (configObj.bookingWindow as any).endDate === 'string' &&
                            String((configObj.bookingWindow as any).endDate)
                          )
                        }
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {(() => {
                          const d = parseDateOnly(
                            isPlainObject(configObj.bookingWindow) ? (configObj.bookingWindow as any).endDate : undefined
                          );
                          return d ? format(d as Date, 'PPP') : 'Pick end date';
                        })()}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        captionLayout="dropdown"
                        disabled={{ before: today }}
                        fromMonth={today}
                        fromYear={today.getFullYear()}
                        selected={parseDateOnly(
                          isPlainObject(configObj.bookingWindow) ? (configObj.bookingWindow as any).endDate : undefined
                        )}
                        onSelect={(d) => {
                          const prev = isPlainObject(configObj.bookingWindow)
                            ? (configObj.bookingWindow as Record<string, unknown>)
                            : {};
                          patchConfig({ bookingWindow: { ...prev, endDate: d ? toDateOnlyString(d) : undefined } });
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div className="grid gap-1">
                <Label>Date range (absolute)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" className="justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {(() => {
                        const start = parseDateOnly(
                          isPlainObject(configObj.bookingWindow) ? (configObj.bookingWindow as any).startDate : undefined
                        );
                        const end = parseDateOnly(
                          isPlainObject(configObj.bookingWindow) ? (configObj.bookingWindow as any).endDate : undefined
                        );
                        if (start && end) return `${format(start as Date, 'PPP')} – ${format(end as Date, 'PPP')}`;
                        if (start) return `${format(start as Date, 'PPP')} – …`;
                        return 'Pick a date range';
                      })()}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="range"
                      captionLayout="dropdown"
                      disabled={{ before: today }}
                      fromMonth={today}
                      fromYear={today.getFullYear()}
                      selected={{
                        from: parseDateOnly(
                          isPlainObject(configObj.bookingWindow) ? (configObj.bookingWindow as any).startDate : undefined
                        ),
                        to: parseDateOnly(
                          isPlainObject(configObj.bookingWindow) ? (configObj.bookingWindow as any).endDate : undefined
                        ),
                      }}
                      onSelect={(range) => {
                        const prev = isPlainObject(configObj.bookingWindow)
                          ? (configObj.bookingWindow as Record<string, unknown>)
                          : {};
                        patchConfig({
                          bookingWindow: {
                            ...prev,
                            startDate: range?.from ? toDateOnlyString(range.from) : undefined,
                            endDate: range?.to ? toDateOnlyString(range.to) : undefined,
                          },
                        });
                      }}
                    />
                  </PopoverContent>
                </Popover>
                <p className="text-[11px] text-muted-foreground">
                  Uses <span className="font-mono">YYYY-MM-DD</span> in config.
                </p>
              </div>
              <div className="flex justify-end">
                <Button type="button" size="sm" variant="outline" onClick={() => patchConfig({ bookingWindow: undefined })}>
                  Clear booking window
                </Button>
              </div>
            </div>

            <div className="grid gap-2 rounded-lg border border-border/60 bg-background p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Restrictions</p>
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="grid gap-1">
                  <Label>Min notice (minutes)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={10080}
                    placeholder="—"
                    value={typeof configObj.minNoticeMinutes === 'number' ? (configObj.minNoticeMinutes as number) : ''}
                    onChange={(e) =>
                      patchConfig({
                        minNoticeMinutes:
                          e.target.value === ''
                            ? undefined
                            : Math.max(0, Math.min(10080, Number.parseInt(e.target.value, 10) || 0)),
                      })
                    }
                  />
                </div>
                <div className="grid gap-1">
                  <Label>Slot generation horizon (days)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={365}
                    placeholder="—"
                    value={
                      typeof configObj.slotGenerationHorizonDays === 'number'
                        ? (configObj.slotGenerationHorizonDays as number)
                        : ''
                    }
                    onChange={(e) =>
                      patchConfig({
                        slotGenerationHorizonDays:
                          e.target.value === ''
                            ? undefined
                            : Math.max(1, Math.min(365, Number.parseInt(e.target.value, 10) || 90)),
                      })
                    }
                  />
                </div>
                <div className="grid gap-1 sm:col-span-2">
                  <Label>Schedule time zone (IANA)</Label>
                  <Input
                    placeholder="e.g. America/New_York"
                    className="font-mono text-xs"
                    value={typeof configObj.scheduleTimeZone === 'string' ? (configObj.scheduleTimeZone as string) : ''}
                    onChange={(e) => patchConfig({ scheduleTimeZone: e.target.value || undefined })}
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Slot and booking times are shown in this zone in the admin UI and builder preview. If empty,{' '}
                    <span className="font-mono">America/New_York</span> is used.
                  </p>
                </div>
              </div>
              <div className="grid gap-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Limits</p>
                <div className="grid gap-2 sm:grid-cols-3">
                  <div className="grid gap-1">
                    <Label>Max / day</Label>
                    <Input
                      type="number"
                      min={1}
                      max={10000}
                      placeholder="—"
                      value={
                        isPlainObject(configObj.limits) && typeof (configObj.limits as any).maxAppointmentsPerDay === 'number'
                          ? Number((configObj.limits as any).maxAppointmentsPerDay)
                          : ''
                      }
                      onChange={(e) => {
                        const prev = isPlainObject(configObj.limits) ? (configObj.limits as Record<string, unknown>) : {};
                        patchConfig({
                          limits: {
                            ...prev,
                            maxAppointmentsPerDay:
                              e.target.value === ''
                                ? undefined
                                : Math.max(1, Math.min(10000, Number.parseInt(e.target.value, 10) || 1)),
                          },
                        });
                      }}
                    />
                  </div>
                  <div className="grid gap-1">
                    <Label>Max / email</Label>
                    <Input
                      type="number"
                      min={1}
                      max={1000}
                      placeholder="—"
                      value={
                        isPlainObject(configObj.limits) &&
                        typeof (configObj.limits as any).maxBookingsPerUserEmail === 'number'
                          ? Number((configObj.limits as any).maxBookingsPerUserEmail)
                          : ''
                      }
                      onChange={(e) => {
                        const prev = isPlainObject(configObj.limits) ? (configObj.limits as Record<string, unknown>) : {};
                        patchConfig({
                          limits: {
                            ...prev,
                            maxBookingsPerUserEmail:
                              e.target.value === ''
                                ? undefined
                                : Math.max(1, Math.min(1000, Number.parseInt(e.target.value, 10) || 1)),
                          },
                        });
                      }}
                    />
                  </div>
                  <div className="grid gap-1">
                    <Label>Max / user</Label>
                    <Input
                      type="number"
                      min={1}
                      max={1000}
                      placeholder="—"
                      value={
                        isPlainObject(configObj.limits) && typeof (configObj.limits as any).maxBookingsPerUserId === 'number'
                          ? Number((configObj.limits as any).maxBookingsPerUserId)
                          : ''
                      }
                      onChange={(e) => {
                        const prev = isPlainObject(configObj.limits) ? (configObj.limits as Record<string, unknown>) : {};
                        patchConfig({
                          limits: {
                            ...prev,
                            maxBookingsPerUserId:
                              e.target.value === ''
                                ? undefined
                                : Math.max(1, Math.min(1000, Number.parseInt(e.target.value, 10) || 1)),
                          },
                        });
                      }}
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button type="button" size="sm" variant="outline" onClick={() => patchConfig({ limits: undefined })}>
                    Clear limits
                  </Button>
                </div>
              </div>
            </div>
            <div className="grid gap-2 rounded-lg border border-border/60 bg-background p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Capacity</p>
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="grid gap-1">
                  <Label>Mode</Label>
                  <Select
                    value={
                      isPlainObject(configObj.capacity) && typeof (configObj.capacity as any).mode === 'string'
                        ? String((configObj.capacity as any).mode)
                        : 'one_on_one'
                    }
                    onValueChange={(v) => {
                      const prev = isPlainObject(configObj.capacity)
                        ? (configObj.capacity as Record<string, unknown>)
                        : {};
                      patchConfig({ capacity: { ...prev, mode: v } });
                    }}
                  >
                    <SelectTrigger className="bg-background">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="one_on_one">One-on-one</SelectItem>
                      <SelectItem value="group">Group</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-1">
                  <Label>Max attendees per slot</Label>
                  <Input
                    type="number"
                    min={1}
                    max={10000}
                    value={
                      isPlainObject(configObj.capacity) &&
                      typeof (configObj.capacity as any).maxAttendeesPerSlot === 'number'
                        ? Number((configObj.capacity as any).maxAttendeesPerSlot)
                        : ''
                    }
                    onChange={(e) => {
                      const prev = isPlainObject(configObj.capacity)
                        ? (configObj.capacity as Record<string, unknown>)
                        : {};
                      patchConfig({
                        capacity: {
                          ...prev,
                          maxAttendeesPerSlot:
                            e.target.value === ''
                              ? undefined
                              : Math.max(1, Math.min(10000, Number.parseInt(e.target.value, 10) || 1)),
                        },
                      });
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="grid gap-2 rounded-lg border border-border/60 bg-background p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Display (UI hints)</p>
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="grid gap-1">
                  <Label>First day of week</Label>
                  <Select
                    value={
                      isPlainObject(configObj.display) && typeof (configObj.display as any).firstDayOfWeek === 'string'
                        ? String((configObj.display as any).firstDayOfWeek)
                        : '__unset__'
                    }
                    onValueChange={(v) => {
                      const prev = isPlainObject(configObj.display) ? (configObj.display as Record<string, unknown>) : {};
                      patchConfig({
                        display: {
                          ...prev,
                          firstDayOfWeek: v === '__unset__' ? undefined : v,
                        },
                      });
                    }}
                  >
                    <SelectTrigger className="bg-background">
                      <SelectValue placeholder="Unset" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__unset__">Unset</SelectItem>
                      <SelectItem value="sunday">Sunday</SelectItem>
                      <SelectItem value="monday">Monday</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-1">
                  <Label>Date format hint</Label>
                  <Input
                    value={
                      isPlainObject(configObj.display) && typeof (configObj.display as any).dateFormatHint === 'string'
                        ? String((configObj.display as any).dateFormatHint)
                        : ''
                    }
                    onChange={(e) => {
                      const prev = isPlainObject(configObj.display) ? (configObj.display as Record<string, unknown>) : {};
                      patchConfig({ display: { ...prev, dateFormatHint: e.target.value || undefined } });
                    }}
                    placeholder="e.g. MMM D, YYYY"
                  />
                </div>
                <div className="grid gap-1">
                  <Label>Time format hint</Label>
                  <Input
                    value={
                      isPlainObject(configObj.display) && typeof (configObj.display as any).timeFormatHint === 'string'
                        ? String((configObj.display as any).timeFormatHint)
                        : ''
                    }
                    onChange={(e) => {
                      const prev = isPlainObject(configObj.display) ? (configObj.display as Record<string, unknown>) : {};
                      patchConfig({ display: { ...prev, timeFormatHint: e.target.value || undefined } });
                    }}
                    placeholder="e.g. h:mm A"
                  />
                </div>
                <div className="grid gap-1">
                  <Label>Location</Label>
                  <Input
                    value={
                      isPlainObject(configObj.display) && typeof (configObj.display as any).location === 'string'
                        ? String((configObj.display as any).location)
                        : ''
                    }
                    onChange={(e) => {
                      const prev = isPlainObject(configObj.display) ? (configObj.display as Record<string, unknown>) : {};
                      patchConfig({ display: { ...prev, location: e.target.value || undefined } });
                    }}
                    placeholder="e.g. Zoom"
                  />
                </div>
                <div className="grid gap-1 sm:col-span-2">
                  <Label>Description</Label>
                  <Textarea
                    rows={3}
                    value={
                      isPlainObject(configObj.display) && typeof (configObj.display as any).description === 'string'
                        ? String((configObj.display as any).description)
                        : ''
                    }
                    onChange={(e) => {
                      const prev = isPlainObject(configObj.display) ? (configObj.display as Record<string, unknown>) : {};
                      patchConfig({ display: { ...prev, description: e.target.value || undefined } });
                    }}
                  />
                </div>
                <div className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-3 py-2">
                  <Label className="text-sm font-normal">Require name</Label>
                  <Switch
                    checked={isPlainObject(configObj.display) && (configObj.display as any).requireName === true}
                    onCheckedChange={(checked) => {
                      const prev = isPlainObject(configObj.display) ? (configObj.display as Record<string, unknown>) : {};
                      patchConfig({ display: { ...prev, requireName: checked ? true : undefined } });
                    }}
                  />
                </div>
                <div className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-3 py-2">
                  <Label className="text-sm font-normal">Require email</Label>
                  <Switch
                    checked={isPlainObject(configObj.display) && (configObj.display as any).requireEmail === true}
                    onCheckedChange={(checked) => {
                      const prev = isPlainObject(configObj.display) ? (configObj.display as Record<string, unknown>) : {};
                      patchConfig({ display: { ...prev, requireEmail: checked ? true : undefined } });
                    }}
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button type="button" size="sm" variant="outline" onClick={() => patchConfig({ display: undefined })}>
                  Clear display hints
                </Button>
              </div>
            </div>

            <div className="grid gap-2 rounded-lg border border-border/60 bg-background p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Integrations</p>
              <div className="grid gap-2">
                <div className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-3 py-2">
                  <Label className="text-sm font-normal">Google Calendar enabled</Label>
                  <Switch
                    checked={
                      isPlainObject(configObj.integrations) &&
                      isPlainObject((configObj.integrations as any).googleCalendar) &&
                      ((configObj.integrations as any).googleCalendar as any).enabled === true
                    }
                    onCheckedChange={(checked) => {
                      const prev = isPlainObject(configObj.integrations)
                        ? (configObj.integrations as Record<string, unknown>)
                        : {};
                      const prevGC =
                        isPlainObject(prev.googleCalendar) ? (prev.googleCalendar as Record<string, unknown>) : {};
                      patchConfig({
                        integrations: {
                          ...prev,
                          googleCalendar: checked ? { ...prevGC, enabled: true } : undefined,
                        },
                      });
                    }}
                  />
                </div>
                {isPlainObject(configObj.integrations) &&
                isPlainObject((configObj.integrations as any).googleCalendar) &&
                ((configObj.integrations as any).googleCalendar as any).enabled === true ? (
                  <div className="grid gap-1">
                    <Label>Google Calendar ID (optional)</Label>
                    <Input
                      value={
                        typeof ((configObj.integrations as any).googleCalendar as any).calendarId === 'string'
                          ? String(((configObj.integrations as any).googleCalendar as any).calendarId)
                          : ''
                      }
                      onChange={(e) => {
                        const prev = isPlainObject(configObj.integrations)
                          ? (configObj.integrations as Record<string, unknown>)
                          : {};
                        const prevGC =
                          isPlainObject(prev.googleCalendar) ? (prev.googleCalendar as Record<string, unknown>) : {};
                        patchConfig({
                          integrations: {
                            ...prev,
                            googleCalendar: { ...prevGC, calendarId: e.target.value || undefined, enabled: true },
                          },
                        });
                      }}
                      placeholder="primary"
                      className="font-mono text-xs"
                    />
                  </div>
                ) : null}
                <div className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-3 py-2">
                  <Label className="text-sm font-normal">Zoom enabled</Label>
                  <Switch
                    checked={
                      isPlainObject(configObj.integrations) &&
                      isPlainObject((configObj.integrations as any).zoom) &&
                      ((configObj.integrations as any).zoom as any).enabled === true
                    }
                    onCheckedChange={(checked) => {
                      const prev = isPlainObject(configObj.integrations)
                        ? (configObj.integrations as Record<string, unknown>)
                        : {};
                      patchConfig({
                        integrations: {
                          ...prev,
                          zoom: checked ? { enabled: true } : undefined,
                        },
                      });
                    }}
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button type="button" size="sm" variant="outline" onClick={() => patchConfig({ integrations: undefined })}>
                  Clear integrations
                </Button>
              </div>
            </div>

            <div className="grid gap-2 rounded-lg border border-border/60 bg-background p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Availability</p>
              <div className="grid gap-1">
                <Label>Weekly schedule (JSON)</Label>
                <Textarea
                  rows={8}
                  className="font-mono text-xs"
                  value={JSON.stringify(configObj.weeklySchedule ?? {}, null, 2)}
                  onChange={(e) => {
                    const raw = e.target.value;
                    try {
                      const parsed = JSON.parse(raw);
                      patchConfig({ weeklySchedule: parsed });
                    } catch {
                      toast.error('Invalid weeklySchedule JSON');
                    }
                  }}
                />
                <p className="text-[11px] text-muted-foreground">
                  Shape: <span className="font-mono">{'{ sun..sat: { enabled, ranges: [{start,end}] } }'}</span> with times
                  as <span className="font-mono">HH:mm</span>.
                </p>
              </div>
            </div>

            <div className="grid gap-2 rounded-lg border border-border/60 bg-background p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Exclusions / overrides</p>
              <div className="grid gap-1">
                <Label>Add blackout date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" className="justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      Pick a date to add
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      captionLayout="dropdown"
                      disabled={{ before: today }}
                      fromMonth={today}
                      fromYear={today.getFullYear()}
                      onSelect={(d) => {
                        if (!d) return;
                        const iso = toDateOnlyString(d);
                        const prev = Array.isArray(configObj.blackoutDates) ? (configObj.blackoutDates as any[]) : [];
                        const next = Array.from(new Set(prev.map(String).concat([iso]))).sort();
                        patchConfig({ blackoutDates: next });
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="grid gap-1">
                <Label>Blackout dates (one per line)</Label>
                <Textarea
                  rows={4}
                  className="font-mono text-xs"
                  value={Array.isArray(configObj.blackoutDates) ? (configObj.blackoutDates as any[]).join('\n') : ''}
                  onChange={(e) => {
                    const lines = e.target.value
                      .split('\n')
                      .map((s) => s.trim())
                      .filter(Boolean);
                    patchConfig({ blackoutDates: lines.length > 0 ? lines : undefined });
                  }}
                  placeholder="YYYY-MM-DD"
                />
              </div>
              <div className="grid gap-1">
                <Label>Lunch breaks (JSON)</Label>
                <Textarea
                  rows={4}
                  className="font-mono text-xs"
                  value={JSON.stringify(configObj.lunchBreaks ?? [], null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      patchConfig({ lunchBreaks: parsed });
                    } catch {
                      toast.error('Invalid lunchBreaks JSON');
                    }
                  }}
                />
              </div>

              <div className="grid gap-2 rounded-lg border border-border/60 bg-muted/10 p-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Date overrides</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  <div className="grid gap-1">
                    <Label>Pick override date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="outline" className="justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {overrideDateKey ? overrideDateKey : 'Pick a date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          captionLayout="dropdown"
                          disabled={{ before: today }}
                          fromMonth={today}
                          fromYear={today.getFullYear()}
                          selected={overrideDateKey ? parseDateOnly(overrideDateKey) : undefined}
                          onSelect={(d) => {
                            if (!d) return;
                            const key = toDateOnlyString(d);
                            setOverrideDateKey(key);
                            const prev = isPlainObject(configObj.dateOverrides)
                              ? (configObj.dateOverrides as Record<string, unknown>)
                              : {};
                            if (!prev[key]) {
                              patchConfig({ dateOverrides: { ...prev, [key]: { ranges: [] } } });
                            }
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                    <p className="text-[11px] text-muted-foreground">
                      Override keys are stored as <span className="font-mono">YYYY-MM-DD</span>.
                    </p>
                  </div>
                  <div className="grid gap-1">
                    <Label>Existing override dates</Label>
                    <Select
                      value={overrideDateKey || '__none__'}
                      onValueChange={(v) => setOverrideDateKey(v === '__none__' ? '' : v)}
                    >
                      <SelectTrigger className="bg-background">
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">—</SelectItem>
                        {Object.keys(isPlainObject(configObj.dateOverrides) ? (configObj.dateOverrides as any) : {})
                          .sort()
                          .map((k) => (
                            <SelectItem key={k} value={k}>
                              {k}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={!overrideDateKey}
                        onClick={() => {
                          if (!overrideDateKey) return;
                          const prev = isPlainObject(configObj.dateOverrides)
                            ? (configObj.dateOverrides as Record<string, unknown>)
                            : {};
                          const next = { ...prev };
                          delete next[overrideDateKey];
                          setOverrideDateKey('');
                          patchConfig({ dateOverrides: Object.keys(next).length > 0 ? next : undefined });
                        }}
                      >
                        Remove override
                      </Button>
                    </div>
                  </div>
                </div>

                {overrideDateKey ? (
                  (() => {
                    const overrides = isPlainObject(configObj.dateOverrides)
                      ? (configObj.dateOverrides as Record<string, unknown>)
                      : {};
                    const current = overrides[overrideDateKey];
                    const currentObj = isPlainObject(current) ? (current as Record<string, unknown>) : {};
                    const isClosed = currentObj.closed === true;
                    const rangesRaw = Array.isArray(currentObj.ranges) ? (currentObj.ranges as unknown[]) : [];
                    const ranges = rangesRaw
                      .map((r) => (isPlainObject(r) ? (r as Record<string, unknown>) : null))
                      .filter((r): r is Record<string, unknown> => Boolean(r))
                      .map((r) => ({
                        start: typeof r.start === 'string' ? r.start : '',
                        end: typeof r.end === 'string' ? r.end : '',
                      }));

                    const updateOverride = (patch: Record<string, unknown>) => {
                      const prev = isPlainObject(configObj.dateOverrides)
                        ? (configObj.dateOverrides as Record<string, unknown>)
                        : {};
                      const prevEntry = isPlainObject(prev[overrideDateKey])
                        ? (prev[overrideDateKey] as Record<string, unknown>)
                        : {};
                      const nextEntry = { ...prevEntry, ...patch };
                      for (const k of Object.keys(patch)) {
                        if (patch[k] === undefined) delete nextEntry[k];
                      }
                      patchConfig({ dateOverrides: { ...prev, [overrideDateKey]: nextEntry } });
                    };

                    return (
                      <div className="grid gap-3 rounded-lg border border-border/60 bg-background p-3">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium">{overrideDateKey}</p>
                          <div className="flex items-center gap-2">
                            <Label className="text-sm font-normal">Closed</Label>
                            <Switch
                              checked={isClosed}
                              onCheckedChange={(checked) =>
                                updateOverride({
                                  closed: checked ? true : undefined,
                                  ranges: checked ? undefined : (currentObj.ranges ?? []),
                                })
                              }
                            />
                          </div>
                        </div>

                        {!isClosed ? (
                          <div className="grid gap-2">
                            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Ranges</p>
                            {ranges.length === 0 ? (
                              <p className="text-[11px] text-muted-foreground">No ranges yet. Add one below.</p>
                            ) : (
                              <div className="grid gap-2">
                                {ranges.map((r, i) => (
                                  <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                                    <div className="grid gap-1">
                                      <Label className="text-[11px]">Start (HH:mm)</Label>
                                      <Input
                                        className="font-mono text-xs"
                                        value={r.start}
                                        onChange={(e) => {
                                          const next = [...ranges];
                                          next[i] = { ...next[i], start: e.target.value };
                                          updateOverride({ ranges: next });
                                        }}
                                      />
                                    </div>
                                    <div className="grid gap-1">
                                      <Label className="text-[11px]">End (HH:mm)</Label>
                                      <Input
                                        className="font-mono text-xs"
                                        value={r.end}
                                        onChange={(e) => {
                                          const next = [...ranges];
                                          next[i] = { ...next[i], end: e.target.value };
                                          updateOverride({ ranges: next });
                                        }}
                                      />
                                    </div>
                                    <Button
                                      type="button"
                                      size="icon"
                                      variant="outline"
                                      className="h-9 w-9"
                                      aria-label="Remove range"
                                      onClick={() => {
                                        const next = ranges.filter((_, idx) => idx !== i);
                                        updateOverride({ ranges: next.length > 0 ? next : [] });
                                      }}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="flex justify-end">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => updateOverride({ ranges: [...ranges, { start: '09:00', end: '17:00' }] })}
                              >
                                Add range
                              </Button>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })()
                ) : (
                  <p className="text-[11px] text-muted-foreground">Pick a date above to edit its override.</p>
                )}
              </div>

              <div className="grid gap-1">
                <Label>Date overrides (JSON record)</Label>
                <Textarea
                  rows={5}
                  className="font-mono text-xs"
                  value={JSON.stringify(configObj.dateOverrides ?? {}, null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      patchConfig({ dateOverrides: parsed });
                    } catch {
                      toast.error('Invalid dateOverrides JSON');
                    }
                  }}
                />
              </div>
            </div>
          </div>
            )}
          </div>
        )}

        {selected.type === 'longText' && (
          <div className="grid gap-3 rounded-xl border border-border/60 bg-muted/10 p-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Long text config</p>
            <div className="grid gap-1">
              <Label>Rows</Label>
              <Input
                type="number"
                min={1}
                placeholder="—"
                value={typeof configObj.rows === 'number' ? (configObj.rows as number) : ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  patchConfig({ rows: raw === '' ? undefined : Math.max(1, Number.parseInt(raw, 10) || 3) });
                }}
              />
            </div>
            <div className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-3 py-2">
              <Label className="text-sm font-normal">Rich text</Label>
              <Switch
                checked={configObj.richText === true}
                onCheckedChange={(checked) => patchConfig({ richText: checked ? true : undefined })}
              />
            </div>
          </div>
        )}

        {['dropdown', 'singleChoice', 'multipleChoice'].includes(selected.type) && (
          <div className="grid gap-3 rounded-xl border border-border/60 bg-muted/10 p-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Choice config</p>
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-3 py-2">
                <Label className="text-sm font-normal">Allow other</Label>
                <Switch
                  checked={configObj.allowOther === true}
                  onCheckedChange={(checked) => patchConfig({ allowOther: checked ? true : undefined })}
                />
              </div>
              <div className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-3 py-2">
                <Label className="text-sm font-normal">Shuffle options</Label>
                <Switch
                  checked={configObj.shuffleOptions === true}
                  onCheckedChange={(checked) => patchConfig({ shuffleOptions: checked ? true : undefined })}
                />
              </div>
            </div>
            {selected.type === 'dropdown' ? (
              <div className="grid gap-1">
                <Label>Placeholder option</Label>
                <Input
                  value={typeof configObj.placeholderOption === 'string' ? (configObj.placeholderOption as string) : ''}
                  onChange={(e) => patchConfig({ placeholderOption: e.target.value || undefined })}
                  placeholder="Select…"
                />
              </div>
            ) : null}
            {selected.type === 'singleChoice' ? (
              <div className="grid gap-1">
                <Label>Display as</Label>
                <Select
                  value={typeof configObj.displayAs === 'string' ? (configObj.displayAs as string) : 'radio'}
                  onValueChange={(v) => patchConfig({ displayAs: v })}
                >
                  <SelectTrigger className="bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="radio">Radio</SelectItem>
                    <SelectItem value="dropdown">Dropdown</SelectItem>
                    <SelectItem value="cards">Cards</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            {selected.type === 'multipleChoice' ? (
              <div className="grid gap-1">
                <Label>Display as</Label>
                <Select
                  value={typeof configObj.displayAs === 'string' ? (configObj.displayAs as string) : 'checkbox'}
                  onValueChange={(v) => patchConfig({ displayAs: v })}
                >
                  <SelectTrigger className="bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="checkbox">Checkbox</SelectItem>
                    <SelectItem value="tiles">Tiles</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : null}
          </div>
        )}

        {selected.type === 'number' && (
          <div className="grid gap-3 rounded-xl border border-border/60 bg-muted/10 p-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Number config</p>
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-3 py-2">
                <Label className="text-sm font-normal">Use grouping</Label>
                <Switch
                  checked={configObj.useGrouping === true}
                  onCheckedChange={(checked) => patchConfig({ useGrouping: checked ? true : undefined })}
                />
              </div>
              <div className="grid gap-1">
                <Label>Decimal places</Label>
                <Input
                  type="number"
                  min={0}
                  placeholder="—"
                  value={typeof configObj.decimalPlaces === 'number' ? (configObj.decimalPlaces as number) : ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    patchConfig({ decimalPlaces: raw === '' ? undefined : Math.max(0, Number.parseInt(raw, 10) || 0) });
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {selected.type === 'fileUpload' && (
          <div className="grid gap-3 rounded-xl border border-border/60 bg-muted/10 p-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">File upload config</p>
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-3 py-2">
                <Label className="text-sm font-normal">Multiple</Label>
                <Switch
                  checked={configObj.multiple === true}
                  onCheckedChange={(checked) => patchConfig({ multiple: checked ? true : undefined })}
                />
              </div>
              <div className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-3 py-2">
                <Label className="text-sm font-normal">Drag & drop</Label>
                <Switch
                  checked={configObj.dragDrop === true}
                  onCheckedChange={(checked) => patchConfig({ dragDrop: checked ? true : undefined })}
                />
              </div>
            </div>
          </div>
        )}

        {selected.type === 'time' && (
          <div className="grid gap-3 rounded-xl border border-border/60 bg-muted/10 p-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Time config</p>
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="grid gap-1">
                <Label>Format</Label>
                <Select
                  value={typeof configObj.format === 'string' ? (configObj.format as string) : '24h'}
                  onValueChange={(v) => patchConfig({ format: v })}
                >
                  <SelectTrigger className="bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="12h">12h</SelectItem>
                    <SelectItem value="24h">24h</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1">
                <Label>Minute step</Label>
                <Input
                  type="number"
                  min={1}
                  placeholder="—"
                  value={typeof configObj.minuteStep === 'number' ? (configObj.minuteStep as number) : ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    patchConfig({ minuteStep: raw === '' ? undefined : Math.max(1, Number.parseInt(raw, 10) || 5) });
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {selected.type === 'submit' && (
          <div className="grid gap-3 rounded-xl border border-border/60 bg-muted/10 p-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Submit config</p>
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="grid gap-1">
                <Label>Variant</Label>
                <Select
                  value={typeof configObj.buttonVariant === 'string' ? (configObj.buttonVariant as string) : 'primary'}
                  onValueChange={(v) => patchConfig({ buttonVariant: v })}
                >
                  <SelectTrigger className="bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="primary">Primary</SelectItem>
                    <SelectItem value="secondary">Secondary</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-3 py-2">
                <Label className="text-sm font-normal">Full width</Label>
                <Switch
                  checked={configObj.fullWidth === true}
                  onCheckedChange={(checked) => patchConfig({ fullWidth: checked ? true : undefined })}
                />
              </div>
            </div>
          </div>
        )}

        {selected.type === 'divider' && (
          <div className="grid gap-3 rounded-xl border border-border/60 bg-muted/10 p-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Divider config</p>
            <div className="grid gap-1">
              <Label>Style</Label>
              <Select
                value={typeof configObj.style === 'string' ? (configObj.style as string) : 'line'}
                onValueChange={(v) => patchConfig({ style: v })}
              >
                <SelectTrigger className="bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="line">Line</SelectItem>
                  <SelectItem value="space">Space</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {(selected.type === 'header' || selected.type === 'pageBreak') && (
          <div className="grid gap-3 rounded-xl border border-border/60 bg-muted/10 p-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Page config</p>
            {selected.type === 'header' ? (
              <div className="grid gap-1">
                <Label>Subheader</Label>
                <Input
                  value={typeof configObj.subHeader === 'string' ? (configObj.subHeader as string) : ''}
                  onChange={(e) => patchConfig({ subHeader: e.target.value || undefined })}
                />
              </div>
            ) : null}
            {selected.type === 'pageBreak' ? (
              <>
                <div className="grid gap-1">
                  <Label>Page title</Label>
                  <Input
                    value={typeof configObj.pageTitle === 'string' ? (configObj.pageTitle as string) : ''}
                    onChange={(e) => patchConfig({ pageTitle: e.target.value || undefined })}
                  />
                </div>
                <div className="grid gap-1">
                  <Label>Page description</Label>
                  <Textarea
                    rows={3}
                    value={typeof configObj.pageDescription === 'string' ? (configObj.pageDescription as string) : ''}
                    onChange={(e) => patchConfig({ pageDescription: e.target.value || undefined })}
                  />
                </div>
              </>
            ) : null}
          </div>
        )}

      </TabsContent>

      <TabsContent value="logic" className="space-y-4 data-[state=inactive]:hidden">
        <FieldLogicConditionsSection
          title="Shown when…"
          description="If you add at least one Show rule, this field is hidden by default until a rule matches."
          action="show"
          emptyCta="Add show rule"
          blocks={blocks}
          setBlocks={setBlocks}
          fieldById={fieldById}
          otherFieldIds={otherFieldIds}
          fieldLabel={fieldLabel}
          getFieldChoiceOptions={getFieldChoiceOptions}
        />
        <FieldLogicConditionsSection
          title="Except hide when…"
          description="Hide rules override Show rules. Use this for exceptions."
          action="hide"
          emptyCta="Add hide rule"
          blocks={blocks}
          setBlocks={setBlocks}
          fieldById={fieldById}
          otherFieldIds={otherFieldIds}
          fieldLabel={fieldLabel}
          getFieldChoiceOptions={getFieldChoiceOptions}
        />
        <FieldLogicConditionsSection
          title="Required when…"
          description="Base required comes from the Required toggle. These rules can require or unrequire the field when visible."
          action="require"
          emptyCta="Add require rule"
          blocks={blocks}
          setBlocks={setBlocks}
          fieldById={fieldById}
          otherFieldIds={otherFieldIds}
          fieldLabel={fieldLabel}
          getFieldChoiceOptions={getFieldChoiceOptions}
        />
        <FieldLogicConditionsSection
          title="Unrequire when…"
          description="Use unrequire rules to make a required field optional under certain conditions."
          action="unrequire"
          emptyCta="Add unrequire rule"
          blocks={blocks}
          setBlocks={setBlocks}
          fieldById={fieldById}
          otherFieldIds={otherFieldIds}
          fieldLabel={fieldLabel}
          getFieldChoiceOptions={getFieldChoiceOptions}
        />
      </TabsContent>
    </Tabs>
  );
}

type FormBuilderProps = {
  form: DynamicForm;
  onFormUpdated: (form: DynamicForm) => void;
};

function normalizeDefinitionSettings(s: unknown): Record<string, unknown> | undefined {
  if (!s || typeof s !== 'object' || Array.isArray(s)) return undefined;
  const o = s as Record<string, unknown>;
  const cleaned: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(o)) {
    if (v !== undefined) cleaned[k] = v;
  }
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
}

function normalizeRules(r: unknown): unknown[] | undefined {
  if (!Array.isArray(r)) return undefined;
  return r.length > 0 ? r : undefined;
}

function logicActionLabel(action: unknown): string {
  const a = typeof action === 'string' ? action : 'show';
  if (a === 'hide') return 'Except hide when';
  if (a === 'require') return 'Required when';
  if (a === 'unrequire') return 'Unrequire when';
  return 'Shown when';
}

/** If the bad string equals an option label, logic probably stored label text instead of option `value`. */
function hintChoiceValueMismatch(field: FormField | undefined, invalidValue: string): string {
  if (!field || field.type !== 'singleChoice') return '';
  const opts = getChoiceOptions(field);
  const needle = invalidValue.trim();
  if (!needle) return '';
  const byLabel = opts.find((o) => o.label.trim().toLowerCase() === needle.toLowerCase());
  if (byLabel) {
    return ` Fix: open this field’s Logic tab, edit that condition, and pick the choice again from the value dropdown (stored id "${byLabel.value}", not the sentence you typed).`;
  }
  const samples = opts.slice(0, 8).map((o) => `"${o.value}"`);
  if (samples.length > 0) {
    const more = opts.length > 8 ? ` … (+${opts.length - 8} more)` : '';
    return ` Valid stored values for that question include: ${samples.join(', ')}${more}. Logic compares these ids, not the labels shown to respondents.`;
  }
  return '';
}

function validateDefinitionForPublish(def: FormDefinition): PublishValidationIssue[] {
  const issues: PublishValidationIssue[] = [];
  const push = (message: string, focus?: Pick<PublishValidationIssue, 'focusFieldId' | 'focusInspectorTab'>) => {
    issues.push(focus ? { message, ...focus } : { message });
  };
  const fields = def.fields ?? [];
  const fieldById = new Map(fields.map((f) => [f.id, f] as const));

  const getSingleChoiceValues = (fieldId: string): Set<string> | null => {
    const f = fieldById.get(fieldId);
    if (!f || f.type !== 'singleChoice') return null;
    return new Set(getChoiceOptions(f).map((o) => o.value));
  };

  // Validate choice option value uniqueness for singleChoice fields.
  for (const f of fields) {
    if (f.type !== 'singleChoice') continue;
    const opts = getChoiceOptions(f);
    const counts = new Map<string, number>();
    for (const o of opts) {
      const key = String(o.value ?? '').trim();
      if (!key) continue;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    const dups = Array.from(counts.entries()).filter(([, c]) => c > 1).map(([v]) => v);
    if (dups.length > 0) {
      push(`Field "${f.label || f.id}" has duplicate option values: ${dups.join(', ')}`, { focusFieldId: f.id });
    }
  }

  const apptFocus = (id: string) => ({ focusFieldId: id } as const);

  // Validate appointment configs (basic required keys + types).
  for (const f of fields) {
    if (f.type !== 'appointment') continue;
    const cfg = f.config;
    if (!cfg || typeof cfg !== 'object' || Array.isArray(cfg)) {
      push(`Appointment field "${f.label || f.id}" is missing config.`, apptFocus(f.id));
      continue;
    }
    const c = cfg as Record<string, unknown>;
    const slotDuration = c.slotDurationMinutes;
    const slotInterval = c.slotIntervalMinutes;
    if (typeof slotDuration !== 'number')
      push(`Appointment field "${f.label || f.id}" is missing slotDurationMinutes (number).`, apptFocus(f.id));
    if (typeof slotInterval !== 'number')
      push(`Appointment field "${f.label || f.id}" is missing slotIntervalMinutes (number).`, apptFocus(f.id));
    const ws = c.weeklySchedule;
    if (!ws || typeof ws !== 'object' || Array.isArray(ws)) {
      push(`Appointment field "${f.label || f.id}" is missing weeklySchedule.`, apptFocus(f.id));
    } else {
      const wso = ws as Record<string, unknown>;
      for (const k of ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const) {
        const day = wso[k];
        if (!day || typeof day !== 'object' || Array.isArray(day)) {
          push(`Appointment field "${f.label || f.id}" weeklySchedule.${k} is missing.`, apptFocus(f.id));
          break;
        }
      }
    }
    const cap = c.capacity;
    if (!cap || typeof cap !== 'object' || Array.isArray(cap)) {
      push(`Appointment field "${f.label || f.id}" is missing capacity.`, apptFocus(f.id));
    } else {
      const capObj = cap as Record<string, unknown>;
      const mode = capObj.mode;
      if (mode !== 'one_on_one' && mode !== 'group') {
        push(`Appointment field "${f.label || f.id}" capacity.mode must be one_on_one or group.`, apptFocus(f.id));
      }
      const max = capObj.maxAttendeesPerSlot;
      if (typeof max !== 'number') {
        push(`Appointment field "${f.label || f.id}" capacity.maxAttendeesPerSlot is missing (number).`, apptFocus(f.id));
      }
    }

    const rescheduleFee = c.rescheduleFeeUsd;
    if (typeof rescheduleFee === 'number' && rescheduleFee > 0) {
      if (rescheduleFee > 10000) {
        push(`Appointment field "${f.label || f.id}" rescheduleFeeUsd must be at most 10000.`, apptFocus(f.id));
      }
      const payFieldId = c.reschedulePaymentFieldId;
      if (typeof payFieldId !== 'string' || !payFieldId.trim()) {
        push(
          `Appointment field "${f.label || f.id}" has a reschedule fee set; select a Square payment field (reschedulePaymentFieldId).`,
          apptFocus(f.id)
        );
      } else {
        const payField = fieldById.get(payFieldId.trim());
        if (!payField || payField.type !== 'payment') {
          push(
            `Appointment field "${f.label || f.id}": reschedule payment id "${payFieldId}" must reference a payment field on this form.`,
            apptFocus(f.id)
          );
        } else {
          const pcfg = payField.config;
          if (!pcfg || typeof pcfg !== 'object' || Array.isArray(pcfg) || (pcfg as Record<string, unknown>).gateway !== 'square') {
            push(`Appointment field "${f.label || f.id}": reschedule payment field must use gateway "square".`, apptFocus(f.id));
          }
        }
      }
    }
  }

  const rescheduleFeeAppointments = fields.filter((f) => {
    if (f.type !== 'appointment') return false;
    const cfg = f.config as Record<string, unknown> | undefined;
    const v = cfg?.rescheduleFeeUsd;
    return typeof v === 'number' && v > 0;
  });
  if (rescheduleFeeAppointments.length >= 2) {
    const payIds = new Set(
      rescheduleFeeAppointments
        .map((f) => (f.config as Record<string, unknown>)?.reschedulePaymentFieldId)
        .filter((x): x is string => typeof x === 'string' && x.length > 0)
    );
    if (payIds.size > 1) {
      push(
        'Multiple appointment fields charge a reschedule fee but use different Square payment fields (RESCHEDULE_PAYMENT_FIELD_CONFLICT). Use the same payment field for all.'
      );
    }
  }

  const logicFocus = (fieldId: string) => ({ focusFieldId: fieldId, focusInspectorTab: 'logic' as const });

  // Validate logic references.
  for (const target of fields) {
    const outlineIndex = fields.findIndex((ff) => ff.id === target.id);
    const outlineHint =
      outlineIndex >= 0
        ? `In the left outline, this is field #${outlineIndex + 1} of ${fields.length} (count from the top). That row is highlighted after you dismiss this message. `
        : '';
    const logic = Array.isArray((target as any).logic) ? ((target as any).logic as unknown[]) : [];
    for (const [i, block] of logic.entries()) {
      if (!block || typeof block !== 'object') continue;
      const rec = block as Record<string, unknown>;
      const actionUi = logicActionLabel(rec.action);
      const when = Array.isArray(rec.when) ? (rec.when as unknown[]) : [];
      for (const [j, clause] of when.entries()) {
        if (!clause || typeof clause !== 'object') continue;
        const where = `${outlineHint}Fix on element "${target.label || target.id}" (type ${target.type}, id ${target.id}) → Logic → "${actionUi}" → rule ${i + 1}, condition ${j + 1}.`;
        const c = clause as Record<string, unknown>;
        const fieldId = typeof c.fieldId === 'string' ? c.fieldId : '';
        const watched = fieldById.get(fieldId);
        const watchedTitle = watched?.label || fieldId;
        if (!fieldId || !fieldById.has(fieldId)) {
          push(`${where} References a missing or deleted field (fieldId).`, logicFocus(target.id));
          continue;
        }
        const op = c.op === 'in' ? 'in' : c.op === 'eq' ? 'eq' : null;
        if (!op) continue;
        const allowed = getSingleChoiceValues(fieldId);
        if (!allowed) continue;
        if (op === 'eq') {
          const v = typeof c.value === 'string' ? c.value : '';
          if (v && !allowed.has(v)) {
            const hint = hintChoiceValueMismatch(watched, v);
            push(
              `${where} Watched single-choice: "${watchedTitle}". The rule compares "${v}", which is not a valid stored option value for that question.${hint}`,
              logicFocus(target.id)
            );
          }
        } else if (op === 'in') {
          const values = Array.isArray(c.values) ? (c.values as unknown[]).map(String) : [];
          for (const v of values) {
            if (v && !allowed.has(v)) {
              const hint = hintChoiceValueMismatch(watched, v);
              push(
                `${where} Watched single-choice: "${watchedTitle}". The rule includes "${v}", which is not a valid stored option value for that question.${hint}`,
                logicFocus(target.id)
              );
            }
          }
        }
      }
    }
  }

  return issues;
}

function draftFingerprint(def: FormDefinition): string {
  return JSON.stringify({
    v: def.schemaVersion,
    t: def.title,
    d: def.description,
    fields: def.fields,
    rules: normalizeRules(def.rules),
    settings: normalizeDefinitionSettings(def.settings),
  });
}


export function FormBuilder({ form, onFormUpdated }: FormBuilderProps) {
  const isMobile = useIsMobile();
  const [formTitle, setFormTitle] = useState(form.title);
  const [formSlug, setFormSlug] = useState(form.slug ?? '');
  const [formDescription, setFormDescription] = useState(form.description ?? '');
  const [schemaVersion] = useState(() => form.draftDefinition?.schemaVersion ?? 1);
  const [fields, setFields] = useState<FormField[]>(() => normalizeFields(form.draftDefinition));
  const [formOptionsOpen, setFormOptionsOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [publishing, setPublishing] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState<'elements' | 'properties'>('elements');
  const [leftTab, setLeftTab] = useState<'outline' | 'elements'>('outline');
  const [outlineQuery, setOutlineQuery] = useState('');
  const [previewDevice, setPreviewDevice] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  const skipNextSave = useRef(true);

  /** Rules from initial load only — do not tie saves to `form.draftDefinition` updates (avoids save→parent update→rebuild→debounce loop). */
  const rulesRef = useRef<unknown[]>((form.draftDefinition?.rules ?? []) as unknown[]);
  const lastSavedFingerprint = useRef<string>('');

  const [formSettings, setFormSettings] = useState<Record<string, unknown>>(() => {
    const s = form.draftDefinition?.settings;
    return s && typeof s === 'object' && !Array.isArray(s) ? { ...(s as Record<string, unknown>) } : {};
  });

  useEffect(() => {
    rulesRef.current = (form.draftDefinition?.rules ?? []) as unknown[];
    const s = form.draftDefinition?.settings;
    setFormSettings(
      s && typeof s === 'object' && !Array.isArray(s) ? { ...(s as Record<string, unknown>) } : {}
    );
    setFormSlug(form.slug ?? '');
    lastSavedFingerprint.current = draftFingerprint({
      schemaVersion: form.draftDefinition?.schemaVersion ?? 1,
      title: form.title.trim() || undefined,
      description: (form.description ?? '').trim() || undefined,
      fields: normalizeFields(form.draftDefinition),
      rules: form.draftDefinition?.rules ?? [],
      settings: normalizeDefinitionSettings(s),
    });
    skipNextSave.current = true;
  }, [form._id]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const filteredFields = useMemo(() => {
    const q = outlineQuery.trim().toLowerCase();
    if (!q) return fields;
    return fields.filter((f) => {
      const label = (f.label ?? '').toString().toLowerCase();
      const type = (f.type ?? '').toString().toLowerCase();
      return label.includes(q) || type.includes(q) || f.id.toLowerCase().includes(q);
    });
  }, [fields, outlineQuery]);
  const filteredFieldIds = useMemo(() => filteredFields.map((f) => f.id), [filteredFields]);

  useEffect(() => {
    if (!selectedId) return;
    // Keep selection visible in outline when filtering
    const isSelectedVisible = filteredFieldIds.includes(selectedId);
    if (!isSelectedVisible) return;
    const el = document.querySelector(`[data-outline-field-id="${selectedId}"]`) as HTMLElement | null;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ block: 'nearest' });
    });
  }, [selectedId, filteredFieldIds]);

  const formIdRef = useRef(form._id);
  formIdRef.current = form._id;
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;
  const formTitleRef = useRef(formTitle);
  formTitleRef.current = formTitle;
  const formSlugRef = useRef(formSlug);
  formSlugRef.current = formSlug;
  const formDescriptionRef = useRef(formDescription);
  formDescriptionRef.current = formDescription;
  const schemaVersionRef = useRef(schemaVersion);
  schemaVersionRef.current = schemaVersion;
  const formSettingsRef = useRef(formSettings);
  formSettingsRef.current = formSettings;
  const onFormUpdatedRef = useRef(onFormUpdated);
  onFormUpdatedRef.current = onFormUpdated;

  const isSavingDraftRef = useRef(false);
  const pendingDraftSaveRef = useRef(false);
  const flushDraftSaveRef = useRef<(() => Promise<void>) | null>(null);

  const buildDefinition = useCallback((): FormDefinition => {
    return {
      schemaVersion,
      title: formTitle.trim() || undefined,
      description: formDescription.trim() || undefined,
      fields,
      rules: normalizeRules(rulesRef.current) ?? [],
      settings: normalizeDefinitionSettings(formSettings),
    };
  }, [schemaVersion, formTitle, formDescription, fields, formSettings]);

  /** Stable: reads latest draft state from refs so debounce deps stay `[fields, formTitle, formDescription]` only. Serializes overlapping saves. */
  const flushDraftSave = useCallback(async () => {
    const def: FormDefinition = {
      schemaVersion: schemaVersionRef.current,
      title: formTitleRef.current.trim() || undefined,
      description: formDescriptionRef.current.trim() || undefined,
      fields: fieldsRef.current,
      rules: rulesRef.current ?? [],
      settings: normalizeDefinitionSettings(formSettingsRef.current),
    };
    const fp = draftFingerprint(def);
    if (fp === lastSavedFingerprint.current) {
      return;
    }

    if (isSavingDraftRef.current) {
      pendingDraftSaveRef.current = true;
      return;
    }

    isSavingDraftRef.current = true;
    pendingDraftSaveRef.current = false;
    setSaveState('saving');
    try {
      const res = await dynamicFormsApi.saveDraft(formIdRef.current, def);
      if (res.statusCode !== 200) {
        throw new Error(res.message || 'Save failed');
      }

      // Keep top-level form metadata title in sync (UI lists/details use `form.title`).
      const nextTitle = formTitleRef.current.trim();
      const serverTitle = res.data.form.title?.trim?.() ? String(res.data.form.title).trim() : '';
      if (nextTitle && nextTitle !== serverTitle) {
        try {
          await dynamicFormsApi.patch(formIdRef.current, { title: nextTitle });
        } catch {
          // Non-blocking: draft definition is saved; title sync can be retried on next autosave.
        }
      }

      // Keep slug in sync if user edited it in the header.
      const nextSlug = formSlugRef.current.trim();
      const serverSlug = res.data.form.slug?.trim?.() ? String(res.data.form.slug).trim() : '';
      if (nextSlug && nextSlug !== serverSlug) {
        try {
          await dynamicFormsApi.patch(formIdRef.current, { slug: nextSlug });
        } catch {
          // Non-blocking.
        }
      }

      lastSavedFingerprint.current = fp;
      onFormUpdatedRef.current(res.data.form);
      setSaveState('saved');
      setTimeout(() => setSaveState((s) => (s === 'saved' ? 'idle' : s)), 2000);
    } catch (e) {
      setSaveState('error');
      toast.error(e instanceof Error ? e.message : 'Could not save draft');
    } finally {
      isSavingDraftRef.current = false;
      if (pendingDraftSaveRef.current) {
        pendingDraftSaveRef.current = false;
        queueMicrotask(() => {
          void flushDraftSaveRef.current?.();
        });
      }
    }
  }, []);

  flushDraftSaveRef.current = flushDraftSave;

  const AUTOSAVE_MS = 2200;

  useEffect(() => {
    if (skipNextSave.current) {
      skipNextSave.current = false;
      return;
    }
    const t = window.setTimeout(() => void flushDraftSaveRef.current?.(), AUTOSAVE_MS);
    return () => window.clearTimeout(t);
  }, [fields, formTitle, formDescription, formSettings]);

  const [pendingInspectorTab, setPendingInspectorTab] = useState<PublishInspectorTab | null>(null);
  const clearPendingInspectorTab = useCallback(() => setPendingInspectorTab(null), []);

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const def = buildDefinition();
      const validationIssues = validateDefinitionForPublish(def);
      if (validationIssues.length > 0) {
        const first = validationIssues[0];
        if (first.focusFieldId) {
          setSelectedId(first.focusFieldId);
          setLeftTab('outline');
          setLeftCollapsed(false);
          setRightCollapsed(false);
          if (first.focusInspectorTab) setPendingInspectorTab(first.focusInspectorTab);
          if (isMobile) {
            setDrawerTab('properties');
            setDrawerOpen(true);
          }
        }
        const hint =
          first.focusFieldId && first.focusInspectorTab === 'logic'
            ? 'That field is selected in the outline and Field properties → Logic is opened so you can fix the condition.'
            : first.focusFieldId
              ? 'That field is selected in the outline.'
              : undefined;
        toast.error(first.message, { ...(hint ? { description: hint } : {}), duration: 16_000 });
        return;
      }
      const draftRes = await dynamicFormsApi.saveDraft(form._id, def);
      if (draftRes.statusCode !== 200) {
        throw new Error(draftRes.message || 'Save before publish failed');
      }
      lastSavedFingerprint.current = draftFingerprint(def);
      onFormUpdated(draftRes.data.form);
      const res = await dynamicFormsApi.publish(form._id);
      if (res.statusCode !== 200) {
        throw new Error(res.message || 'Publish failed');
      }
      onFormUpdated(res.data.form);
      toast.success('Form published');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  const updateField = useCallback((id: string, patch: Partial<FormField>) => {
    setFields((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  }, []);

  const deleteField = useCallback((id: string) => {
    setFields((prev) => prev.filter((f) => f.id !== id));
    setSelectedId((s) => (s === id ? null : s));
  }, []);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;

    if (isPaletteDragId(active.id)) {
      const fieldType = fieldTypeFromPaletteId(active.id);
      if (!fieldType) return;
      const newField = createDefaultField(fieldType);
      setFields((prev) => {
        if (over.id === CANVAS_DROP_ID) {
          return [...prev, newField];
        }
        const overIndex = prev.findIndex((f) => f.id === over.id);
        if (overIndex < 0) return prev;
        const next = [...prev];
        next.splice(overIndex, 0, newField);
        return next;
      });
      setSelectedId(newField.id);
      return;
    }

    if (active.id === over.id) return;

    setFields((prev) => {
      if (over.id === CANVAS_DROP_ID) {
        const oldIndex = prev.findIndex((f) => f.id === active.id);
        if (oldIndex < 0) return prev;
        const copy = [...prev];
        const [item] = copy.splice(oldIndex, 1);
        copy.push(item);
        return copy;
      }
      const oldIndex = prev.findIndex((f) => f.id === active.id);
      const newIndex = prev.findIndex((f) => f.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const handleDragCancel = () => setActiveId(null);

  const overlayField =
    activeId && !isPaletteDragId(activeId) ? fields.find((f) => f.id === activeId) : undefined;

  const addFieldAt = useCallback(
    (type: FormFieldTypeId, opts?: { insertAfterId?: string; closeDrawer?: boolean }) => {
      const newField = createDefaultField(type);
      setFields((prev) => {
        const afterId = opts?.insertAfterId;
        if (!afterId) return [...prev, newField];
        const idx = prev.findIndex((f) => f.id === afterId);
        if (idx < 0) return [...prev, newField];
        const next = [...prev];
        next.splice(idx + 1, 0, newField);
        return next;
      });
      setSelectedId(newField.id);
      if (opts?.closeDrawer) setDrawerOpen(false);
    },
    [setDrawerOpen]
  );

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-3 overflow-hidden px-4 py-4 sm:px-6 sm:py-6">
      <div className="flex shrink-0 flex-col gap-3 border-b border-border/60 pb-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
          <Button variant="ghost" size="sm" className="w-fit gap-2 px-0 text-muted-foreground" asChild>
            <Link to="/f">
              <ArrowLeft className="h-4 w-4" />
              Forms
            </Link>
          </Button>
          <div className="hidden h-6 w-px bg-border sm:block" />
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3">
            <div className="flex items-center gap-2">
              <Input
                className="h-9 max-w-xs border-transparent bg-muted/40 font-semibold shadow-none focus-visible:bg-background"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                maxLength={500}
                aria-label="Form title"
              />
              <Badge variant="secondary" className="capitalize">
                {form.status}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Slug</span>
              <Input
                className="h-9 w-[260px] border-transparent bg-muted/30 font-mono text-xs shadow-none focus-visible:bg-background"
                value={formSlug}
                onChange={(e) => setFormSlug(e.target.value)}
                placeholder="e.g. clinical-schedule-form"
                maxLength={200}
                aria-label="Form slug"
                onBlur={() => {
                  const next = formSlug.trim();
                  if (!next) return;
                  // Save immediately so list view updates quickly.
                  void dynamicFormsApi.patch(form._id, { slug: next }).catch(() => {});
                }}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {saveState === 'saving' && (
              <span className="inline-flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                Saving…
              </span>
            )}
            {saveState === 'saved' && 'Saved'}
            {saveState === 'error' && 'Save failed'}
            {saveState === 'idle' && 'Changes save automatically'}
          </span>
          <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => void flushDraftSave()}>
            <Save className="h-4 w-4" />
            Save now
          </Button>
          <Button type="button" size="sm" className="gap-1.5 shadow-sm" disabled={publishing} onClick={() => void handlePublish()}>
            {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
            Publish
          </Button>
          <Button type="button" variant="ghost" size="sm" asChild>
            <Link to={`/f/${form._id}`}>View</Link>
          </Button>
        </div>
      </div>

      <Dialog open={formOptionsOpen} onOpenChange={setFormOptionsOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Form settings</DialogTitle>
            <DialogDescription>
              Description, thank-you message, redirect, and captcha.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh] pr-3">
            <div className="py-1">
              <FormOptionsSidebarContent
                formDescription={formDescription}
                onFormDescriptionChange={setFormDescription}
                formSettings={formSettings}
                onFormSettingsChange={setFormSettings}
              />
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div
          className={cn(
            'grid h-full min-h-0 flex-1 gap-3 overflow-hidden lg:gap-3',
            leftCollapsed && rightCollapsed
              ? 'lg:grid-cols-[1fr]'
              : leftCollapsed
                ? 'lg:grid-cols-[1fr_520px]'
                : rightCollapsed
                  ? 'lg:grid-cols-[460px_1fr]'
                  : 'lg:grid-cols-[460px_1fr_520px]'
          )}
        >
          <aside className={cn('hidden min-h-0 flex-col gap-4 lg:flex', leftCollapsed && 'lg:hidden')}>
            <Card className="flex min-h-0 flex-1 flex-col gap-0 py-0 shadow-sm">
              <CardHeader className="shrink-0 border-b border-border/60 px-4 py-3">
                <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Builder
                </CardTitle>
                <CardDescription className="text-[11px]">Outline, reorder, and add fields.</CardDescription>
              </CardHeader>
              <CardContent className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
                <Tabs
                  value={leftTab}
                  onValueChange={(v) => setLeftTab(v as typeof leftTab)}
                  className="flex min-h-0 flex-1 flex-col"
                >
                  <TabsList className="grid w-full grid-cols-2 shrink-0 rounded-none border-b border-border/60">
                    <TabsTrigger value="outline">Outline</TabsTrigger>
                    <TabsTrigger value="elements">Elements</TabsTrigger>
                  </TabsList>
                  <TabsContent
                    value="outline"
                    className="min-h-0 flex-1 overflow-hidden data-[state=inactive]:hidden"
                  >
                    <ScrollArea className="h-full">
                      <div className="p-4">
                        <div className="mb-3 grid gap-2">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-[11px] font-medium text-muted-foreground">
                              Fields ({fields.length})
                            </p>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-8 gap-1.5"
                              onClick={() => setLeftTab('elements')}
                            >
                              <Plus className="h-4 w-4" />
                              Add
                            </Button>
                          </div>
                          <div className="flex items-center gap-2">
                            <Input
                              value={outlineQuery}
                              onChange={(e) => setOutlineQuery(e.target.value)}
                              placeholder="Search fields…"
                              className="h-9"
                            />
                            {outlineQuery.trim() ? (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-9"
                                onClick={() => setOutlineQuery('')}
                              >
                                Clear
                              </Button>
                            ) : null}
                          </div>
                          {outlineQuery.trim() ? (
                            <p className="text-[11px] text-muted-foreground">
                              Showing {filteredFields.length} of {fields.length}
                            </p>
                          ) : null}
                        </div>
                        <CanvasDropArea>
                          <SortableContext items={filteredFieldIds} strategy={verticalListSortingStrategy}>
                            {filteredFields.length === 0 ? (
                              <div className="flex min-h-[220px] flex-col items-center justify-center gap-2 text-center">
                                <p className="text-sm font-medium text-muted-foreground">
                                  {fields.length === 0 ? 'No fields yet' : 'No matches'}
                                </p>
                                <p className="max-w-sm text-xs text-muted-foreground">
                                  {fields.length === 0
                                    ? 'Switch to Elements to add your first field.'
                                    : 'Try a different search term.'}
                                </p>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {filteredFields.map((field) => (
                                  <SortableFieldRow
                                    key={field.id}
                                    field={field}
                                    selected={selectedId === field.id}
                                    index={fields.findIndex((f) => f.id === field.id)}
                                    total={fields.length}
                                    onMove={({ id, direction }) => {
                                      setFields((prev) => {
                                        const oldIndex = prev.findIndex((f) => f.id === id);
                                        if (oldIndex < 0) return prev;
                                        const nextIndex =
                                          direction === 'up' ? Math.max(0, oldIndex - 1) : Math.min(prev.length - 1, oldIndex + 1);
                                        if (nextIndex === oldIndex) return prev;
                                        return arrayMove(prev, oldIndex, nextIndex);
                                      });
                                    }}
                                    onDelete={(id) => {
                                      deleteField(id);
                                    }}
                                    onSelect={() => {
                                      setSelectedId(field.id);
                                      if (isMobile) {
                                        setDrawerTab('properties');
                                        setDrawerOpen(true);
                                      }
                                    }}
                                  />
                                ))}
                              </div>
                            )}
                          </SortableContext>
                        </CanvasDropArea>
                      </div>
                    </ScrollArea>
                  </TabsContent>
                  <TabsContent
                    value="elements"
                    className="min-h-0 flex-1 overflow-hidden data-[state=inactive]:hidden"
                  >
                    <ScrollArea className="h-full pr-3">
                      <div className="p-4">
                        <FormElementsPalette
                          onAddField={(t) => {
                            addFieldAt(t, { insertAfterId: selectedId ?? undefined });
                            setLeftTab('outline');
                          }}
                        />
                      </div>
                    </ScrollArea>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </aside>

          <div className="flex min-h-0 min-w-0 flex-col overflow-hidden lg:justify-self-center lg:w-full lg:max-w-[860px]">
            <Card className="flex min-h-0 flex-1 flex-col gap-0 py-0 shadow-sm">
              <CardHeader className="shrink-0 border-b border-border/60 px-4 py-3 sm:px-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Live preview
                    </CardTitle>
                    <CardDescription className="text-[11px]">
                      This is what respondents will see (no submit).
                    </CardDescription>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-1 rounded-lg border border-border/60 bg-muted/10 p-1">
                      <Button
                        type="button"
                        variant={previewDevice === 'mobile' ? 'secondary' : 'ghost'}
                        size="sm"
                        className="h-8"
                        onClick={() => setPreviewDevice('mobile')}
                      >
                        Mobile
                      </Button>
                      <Button
                        type="button"
                        variant={previewDevice === 'tablet' ? 'secondary' : 'ghost'}
                        size="sm"
                        className="h-8"
                        onClick={() => setPreviewDevice('tablet')}
                      >
                        Tablet
                      </Button>
                      <Button
                        type="button"
                        variant={previewDevice === 'desktop' ? 'secondary' : 'ghost'}
                        size="sm"
                        className="h-8"
                        onClick={() => setPreviewDevice('desktop')}
                      >
                        Desktop
                      </Button>
                    </div>
                    <div className="hidden items-center gap-1 lg:flex">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8"
                        onClick={() => setLeftCollapsed((v) => !v)}
                      >
                        {leftCollapsed ? 'Show fields' : 'Hide fields'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8"
                        onClick={() => setRightCollapsed((v) => !v)}
                      >
                        {rightCollapsed ? 'Show props' : 'Hide props'}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="relative flex min-h-0 flex-1 flex-col overflow-hidden p-0">
                <ScrollArea className="h-full min-h-0 flex-1">
                  <div className="p-3">
                    <div
                      className={cn(
                        'rounded-2xl border border-border/60 bg-card p-3 shadow-sm sm:p-4',
                        previewDevice === 'mobile' && 'mx-auto max-w-[420px]',
                        previewDevice === 'tablet' && 'mx-auto max-w-[820px]',
                        previewDevice === 'desktop' && 'mx-auto max-w-[760px]'
                      )}
                    >
                      <FormPreview
                        definition={buildDefinition()}
                        mode="respond"
                        selectedId={selectedId}
                        onSelectFieldId={(fieldId) => {
                          setSelectedId(fieldId);
                          if (isMobile) {
                            setDrawerTab('properties');
                            setDrawerOpen(true);
                          }
                        }}
                      />
                    </div>
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          <aside
            className={cn(
              'hidden min-h-0 w-[520px] shrink-0 flex-col gap-4 border-l border-border/60 lg:flex lg:flex-col',
              rightCollapsed && 'lg:hidden'
            )}
          >
            <Card className="flex min-h-0 min-w-0 flex-1 flex-col gap-0 py-0 shadow-sm">
              <CardHeader className="shrink-0 border-b border-border/60 px-4 py-3 sm:px-5">
                <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Field properties
                </CardTitle>
                <CardDescription className="text-[11px]">Edit the selected field.</CardDescription>
              </CardHeader>
              <CardContent className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
                <ScrollArea className="min-h-0 flex-1">
                  <div className="px-4 py-3 sm:px-5">
                    <FieldPropertiesInspector
                      fields={fields}
                      selectedId={selectedId}
                      onUpdateField={updateField}
                      onDeleteField={deleteField}
                      pendingInspectorTab={pendingInspectorTab}
                      onPendingInspectorTabConsumed={clearPendingInspectorTab}
                    />
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </aside>
        </div>

        <Drawer open={drawerOpen} onOpenChange={setDrawerOpen} direction="right" shouldScaleBackground={false}>
          <DrawerContent hideOverlay className="!max-h-[92vh] !w-[min(100vw,380px)]">
            <DrawerHeader className="relative border-b border-border/60 text-left">
              <DrawerTitle>Build form</DrawerTitle>
              <DrawerClose asChild>
                <Button variant="ghost" size="sm" className="absolute right-2 top-2">
                  Close
                </Button>
              </DrawerClose>
            </DrawerHeader>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-6 pt-2">
              <Tabs
                value={drawerTab}
                onValueChange={(v) => setDrawerTab(v as typeof drawerTab)}
                className="flex h-[min(72vh,560px)] min-h-0 flex-col"
              >
                <TabsList className="grid w-full grid-cols-2 shrink-0">
                  <TabsTrigger value="elements">Elements</TabsTrigger>
                  <TabsTrigger value="properties">Props</TabsTrigger>
                </TabsList>
                <TabsContent
                  value="elements"
                  className="mt-3 min-h-0 flex-1 overflow-hidden data-[state=inactive]:hidden"
                >
                  <Card className="gap-0 py-0 shadow-sm">
                    <CardHeader className="border-b border-border/60 px-3 py-3">
                      <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Form elements
                      </CardTitle>
                      <CardDescription className="text-[11px]">Drag into the canvas.</CardDescription>
                    </CardHeader>
                    <CardContent className="p-0">
                      <ScrollArea className="h-[min(52vh,420px)] pr-3">
                        <div className="px-3 py-3">
                          <FormElementsPalette
                            onAddField={(t) =>
                              addFieldAt(t, { insertAfterId: selectedId ?? undefined, closeDrawer: true })
                            }
                          />
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                </TabsContent>
                <TabsContent
                  value="properties"
                  className="mt-3 min-h-0 flex-1 overflow-hidden data-[state=inactive]:hidden"
                >
                  <Card className="flex h-[min(58vh,480px)] min-h-0 flex-col gap-0 py-0 shadow-sm">
                    <CardHeader className="shrink-0 border-b border-border/60 px-3 py-3">
                      <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Field properties
                      </CardTitle>
                      <CardDescription className="text-[11px]">Edit the selected field.</CardDescription>
                    </CardHeader>
                    <CardContent className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
                      <FieldPropertiesInspector
                        fields={fields}
                        selectedId={selectedId}
                        onUpdateField={updateField}
                        onDeleteField={deleteField}
                        pendingInspectorTab={pendingInspectorTab}
                        onPendingInspectorTabConsumed={clearPendingInspectorTab}
                      />
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          </DrawerContent>
        </Drawer>

        <DragOverlay dropAnimation={null}>
          {activeId && isPaletteDragId(activeId) ? (
            <Card className="w-64 border-primary/30 p-3 shadow-lg">
              <span className="text-sm font-medium">
                {PALETTE_GROUPS.flatMap((g) => g.items).find((i) => paletteDragId(i.type) === activeId)?.label ??
                  'Field'}
              </span>
            </Card>
          ) : overlayField ? (
            <Card className="w-full max-w-md border-primary/30 p-3 opacity-95 shadow-lg">
              <Badge variant="secondary" className="mb-1 text-[10px] uppercase">
                {overlayField.type}
              </Badge>
              <p className="text-sm font-medium">{fieldSummary(overlayField)}</p>
            </Card>
          ) : null}
        </DragOverlay>
      </DndContext>
      </div>

      <Button
        type="button"
        size="icon"
        className="fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full shadow-lg lg:hidden"
        onClick={() => {
          setDrawerTab(selectedId ? 'properties' : 'elements');
          setDrawerOpen(true);
        }}
        aria-label="Open form elements"
      >
        <PanelRight className="h-6 w-6" />
      </Button>
    </div>
  );
}
